import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Plus, Phone, Mail, Linkedin, Star, MessageSquare, Calendar, ChevronRight, Trash2, Edit3, Check, Settings, LogOut, Eye, EyeOff, Clock, Send, Zap, Sparkles, Users, Newspaper, History, Home, Menu, Search, Globe, CheckSquare, TrendingUp, BarChart3, GraduationCap, CreditCard, ClipboardCheck } from 'lucide-react';
import LeadDocumentsSection from './crm/LeadDocumentsSection';
import ContentAgent from './ContentAgent';
import AnalyticsDashboard from './AnalyticsDashboard';
import MasterclassAdmin from './MasterclassAdmin';
import OrdersAdmin from './OrdersAdmin';
import ReadyChecksAdmin from './ReadyChecksAdmin';
import KundenModuleBoard from './KundenModuleBoard';
import LongOperationOverlay from './ui/LongOperationOverlay';
import useAsyncTask from '../hooks/useAsyncTask';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('crm_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Wraps panel content: either as full modal (overlay) or inline (filling parent)
function Panel({ inline, onClose, maxWidth = 'max-w-2xl', children }) {
  if (inline) {
    return (
      <div className="flex flex-col bg-white flex-1 min-h-0 overflow-y-auto">
        {children}
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className={`w-full ${maxWidth} bg-white h-full overflow-y-auto flex flex-col shadow-2xl`}>
        {children}
      </div>
    </div>
  );
}

const COLUMNS = [
  { id: 'masterclass',  label: 'Masterclass',        color: '#e07a2f' },
  { id: 'neu',          label: 'Neu',               color: '#6b7280' },
  { id: 'kontaktiert',  label: 'Kontaktiert',       color: '#a78bfa' },
  { id: 'qualifiziert', label: 'Qualifiziert',       color: '#3b82f6' },
  { id: 'termin',       label: 'Termin',             color: '#8b5cf6' },
  { id: 'angebot',      label: 'Angebot',            color: '#f59e0b' },
  { id: 'kunde',        label: 'Kunde',              color: '#10b981' },
  { id: 'verloren',     label: 'Verloren',           color: '#ef4444' },
];

const CUSTOMER_COLUMNS = [
  { id: 'setup_call', label: 'Setup Call',          color: '#8b5cf6' },
  { id: 'schulung',   label: 'Mitarbeiter-Schulung', color: '#3b82f6' },
  { id: 'followup',   label: 'Follow-up',            color: '#10b981' },
];

function followupColor(interactions) {
  const meetings = (interactions || []).filter(i => i.type === 'meeting');
  if (!meetings.length) return '#ef4444'; // never had meeting = red
  const last = new Date(meetings[meetings.length - 1].created_at);
  const days = (Date.now() - last.getTime()) / 86400000;
  if (days < 120) return '#10b981'; // < 4 months = green
  if (days < 180) return '#f59e0b'; // 4-6 months = yellow
  return '#ef4444';                  // > 6 months = red
}

const SCORE_COLORS = { hot: '#ef4444', warm: '#f59e0b', cold: '#6b7280', unknown: '#9ca3af' };
const SCORE_LABELS = { hot: '🔥 Hot', warm: '🌡 Warm', cold: '❄️ Cold', unknown: '?' };

const INTERACTION_ICONS = {
  chatbot: <MessageSquare size={13} />, email: <Mail size={13} />,
  linkedin: <Linkedin size={13} />, phone: <Phone size={13} />,
  meeting: <Calendar size={13} />, note: <Edit3 size={13} />, system: <ChevronRight size={13} />,
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ── Lead Card ────────────────────────────────────────────────────────────────
function LeadCard({ lead, onOpen, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, lead.id)}
      onClick={() => onOpen(lead)}
      className="bg-white border border-gray-200 rounded-lg p-3 mb-2 cursor-pointer hover:border-[#befa0a] hover:shadow-sm transition-all select-none"
    >
      <div className="flex items-start justify-between mb-1">
        <p className="text-[#1f1f1f] text-sm font-bold leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {lead.company_name || 'Unbekannt'}
        </p>
        <span className="text-[10px] font-bold ml-2 flex-shrink-0" style={{ color: SCORE_COLORS[lead.score] || '#9ca3af' }}>
          {SCORE_LABELS[lead.score] || '?'}
        </span>
      </div>
      {lead.contact_name && (
        <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {lead.contact_name} {lead.contact_lastname || ''}
        </p>
      )}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {lead.source && (
          <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
            {lead.source}
          </span>
        )}
        {lead.interactions?.length > 0 && (
          <span className="text-[10px] text-gray-400">{lead.interactions.length} Aktionen</span>
        )}
      </div>
    </div>
  );
}

// ── EnBet 3-Modul-Projekt-Tracking (Kunden) ──────────────────────────────────
const PROJECT_MODULES = [
  { num: 1, key: 'module1', label: 'AI Ready Check', sub: 'Diagnose & Schmerz-Validierung' },
  { num: 2, key: 'module2', label: 'KI Strategie Workshop', sub: 'Audit-Report & Roadmap' },
  { num: 3, key: 'module3', label: 'Implementation', sub: 'Live-Agents & Skalierung' },
];
const MODULE_STATUS_META = {
  offen: { label: 'Gebucht', cls: 'bg-gray-100 text-gray-500' },
  laeuft: { label: 'Im Prozess', cls: 'bg-yellow-100 text-yellow-700' },
  fertig: { label: 'Abgeschlossen', cls: 'bg-emerald-100 text-emerald-700' },
};

// Modul-1-Panel: AI Ready Check starten, Link teilen, erhobene Metriken zeigen
const DOMAIN_LABELS_DE = {
  prozess: 'Prozesse',
  controlling: 'Controlling',
  operations: 'Operations',
  marketing: 'Marketing',
  strategie: 'Strategie',
  hr: 'HR',
};

function MilestoneList({ milestones, onChange, title }) {
  const [busyId, setBusyId] = useState(null);

  const toggle = async (m) => {
    setBusyId(m.id);
    try {
      await axios.patch(`${API}/admin/milestones/${m.id}`, { done: !m.done_at }, { headers: authHeaders() });
      onChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Konnte Termin nicht aktualisieren');
    }
    setBusyId(null);
  };

  return (
    <div className="bg-white border border-gray-200 rounded p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        {title || 'Termine · Häkchen schaltet Chatbots frei'}
      </p>
      <ol className="space-y-1.5">
        {milestones.map((m) => {
          const done = !!m.done_at;
          const unlocks = (m.unlocks_domains || []).map((d) => DOMAIN_LABELS_DE[d] || d).join(', ');
          return (
            <li key={m.id} className={`flex items-start gap-2 p-2 rounded ${done ? 'bg-emerald-50/50' : 'bg-gray-50/50'}`}>
              <button onClick={() => toggle(m)} disabled={busyId === m.id}
                aria-label={done ? 'Erledigt zurücknehmen' : 'Als erledigt markieren'}
                className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center text-[10px] font-bold transition-colors ${
                  done ? 'bg-[#658a07] border-[#658a07] text-white' : 'bg-white border-gray-300 hover:border-[#658a07]'
                } disabled:opacity-50`}
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {done ? '✓' : ''}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${done ? 'text-emerald-800' : 'text-[#1f1f1f]'}`}>
                  {m.step_order}. {m.title}
                </p>
                {m.description && <p className="text-[10px] text-gray-500 leading-snug">{m.description}</p>}
                {unlocks && (
                  <p className="text-[10px] text-[#658a07] mt-0.5">
                    Schaltet frei: <strong>{unlocks}</strong>
                  </p>
                )}
                {done && m.done_at && (
                  <p className="text-[10px] text-emerald-600 mt-0.5">
                    Erledigt: {new Date(m.done_at).toLocaleDateString('de-DE')}
                    {m.done_by ? ` · ${m.done_by}` : ''}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Module1Panel({ leadId, readonly }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [startResult, setStartResult] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/leads/${leadId}/modul-1-status`, { headers: authHeaders() })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ has_customer: false, has_ready_check: false, portal_url: '' }))
      .finally(() => setLoading(false));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  // Kohorten laden für Auswahl-Dropdown
  useEffect(() => {
    if (readonly) return;
    axios.get(`${API}/admin/cohorts`, { headers: authHeaders() })
      .then(({ data }) => setCohorts(Array.isArray(data) ? data.filter((c) => c.status !== 'archived' && c.status !== 'completed') : []))
      .catch(() => setCohorts([]));
  }, [readonly]);

  const start = async () => {
    if (!selectedCohortId) { alert('Bitte Kohorte wählen'); return; }
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/${leadId}/start-modul-1`,
        { send_mail: true, cohort_id: selectedCohortId },
        { headers: authHeaders() });
      setStartResult(data);
      setConfirmStart(false);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Modul-1-Start');
    }
    setStarting(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (loading) return <p className="text-[11px] text-gray-400 mb-2">Lädt…</p>;

  // Bereits gestartet — Status + Milestone-Verwaltung
  if (status?.has_ready_check) {
    const rc = status.ready_check;
    return (
      <div className="mb-3 space-y-2">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              ✓ Modul 1 läuft
            </p>
            <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{rc.status}</span>
          </div>
          <p className="text-[11px] text-gray-600 mb-1">Kunde: <strong>{status.customer_email}</strong></p>
          {status.cohort && (
            <p className="text-[11px] text-gray-600 mb-1">Kohorte: <strong>{status.cohort.name}</strong></p>
          )}
          <p className="text-[11px] text-gray-600 mb-2 font-mono">{rc.run_id}</p>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-white border border-gray-200 rounded py-1.5">
              <div className="font-bold text-[#1f1f1f]">{status.milestones_done}/{status.milestones_total}</div>
              <div className="text-[10px] text-gray-500">Termine</div>
            </div>
            <div className="bg-white border border-gray-200 rounded py-1.5">
              <div className="font-bold text-[#1f1f1f]">{status.sessions_unlocked}/{status.sessions_total}</div>
              <div className="text-[10px] text-gray-500">Sessions frei</div>
            </div>
            <div className="bg-white border border-gray-200 rounded py-1.5">
              <div className="font-bold text-[#658a07]">{status.sessions_approved}</div>
              <div className="text-[10px] text-gray-500">freigegeben</div>
            </div>
          </div>
          {rc.report_ready && (
            <p className="text-[11px] text-[#658a07] font-bold mt-2">Report ist fertig — im Tab "Ready Checks" einsehbar</p>
          )}
        </div>

        {/* Kickoff (1:1, hier abhakbar) */}
        {(status.kickoff_milestones || []).length > 0 && (
          <MilestoneList milestones={status.kickoff_milestones} onChange={load} title="Kickoff (1:1)" />
        )}

        {/* Cohort-Milestones (read-only — Verwaltung in der Kohorten-View) */}
        {(status.cohort_milestones || []).length > 0 && (
          <div className="bg-white border border-gray-200 rounded p-2.5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Gruppen-Termine (Kohorten-Ebene)
              </p>
              <span className="text-[10px] text-gray-400 italic">verwaltet unter Modul-1-Kohorten</span>
            </div>
            <ul className="space-y-1">
              {status.cohort_milestones.map((m) => {
                const done = !!m.done_at;
                const unlocks = (m.unlocks_domains || []).map((d) => DOMAIN_LABELS_DE[d] || d).join(', ');
                return (
                  <li key={m.id} className={`flex items-start gap-2 p-2 rounded ${done ? 'bg-emerald-50/40' : 'bg-gray-50/40'}`}>
                    <div className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center text-[10px] font-bold ${
                      done ? 'bg-[#658a07] border-[#658a07] text-white' : 'bg-white border-gray-300'
                    }`}>
                      {done ? '✓' : ''}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold ${done ? 'text-emerald-800' : 'text-[#1f1f1f]'}`}>{m.title}</p>
                      {unlocks && (
                        <p className="text-[10px] text-[#658a07] mt-0.5">Schaltet frei: <strong>{unlocks}</strong></p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Noch nicht gestartet — Start-Button
  return (
    <div className="mb-3 space-y-2">
      {!startResult && !confirmStart && !readonly && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Modul 1 für diesen Kunden starten
          </p>
          <p className="text-[11px] text-gray-600 mb-3">
            Legt einen Portal-Zugang an und schickt eine Welcome-Mail. Ready-Check + Kickoff + 6 Chatbot-Sessions
            werden vorbereitet. Gruppen-Termine kommen aus der gewählten Kohorte.
          </p>

          <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">In welche Kohorte?</label>
          {cohorts.length === 0 ? (
            <p className="text-[11px] text-amber-700 mb-2">
              Noch keine offene Kohorte vorhanden. Lege zuerst eine an unter <strong>Modul-1-Kohorten</strong> in der Sidebar.
            </p>
          ) : (
            <select value={selectedCohortId} onChange={(e) => setSelectedCohortId(e.target.value)}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#befa0a] mb-3">
              <option value="">— Kohorte wählen —</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.member_count} Kunden · {c.status})</option>
              ))}
            </select>
          )}

          <button onClick={() => setConfirmStart(true)} disabled={!selectedCohortId}
            className="text-xs bg-[#0a0a0a] text-[#befa0a] px-4 py-1.5 rounded font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-40"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Modul 1 starten
          </button>
        </div>
      )}

      {confirmStart && (
        <div className="bg-amber-50 border border-amber-300 rounded p-3">
          <p className="text-[11px] text-amber-900 mb-3">
            <strong>Sicher?</strong> Es wird eine Welcome-Mail mit Login-Daten verschickt.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setConfirmStart(false)}
              className="text-xs border border-gray-300 px-3 py-1.5 rounded text-gray-600 hover:bg-gray-100"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Abbrechen
            </button>
            <button onClick={start} disabled={starting}
              className="text-xs bg-[#0a0a0a] text-[#befa0a] px-4 py-1.5 rounded font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {starting ? 'Starte…' : 'Ja, starten + Mail schicken'}
            </button>
          </div>
        </div>
      )}

      {startResult && (
        <div className="bg-[#0a0a0a] text-white rounded p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#befa0a] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            ✓ Modul 1 gestartet
          </p>
          <div className="text-[11px] space-y-1 text-gray-200">
            <p>Kunde: <strong className="text-white">{startResult.customer.email}</strong></p>
            {startResult.mail_sent ? (
              <p className="text-[#befa0a]">✓ Welcome-Mail wurde versendet.</p>
            ) : startResult.initial_password ? (
              <>
                <p className="text-amber-300">⚠ Mail-Versand fehlgeschlagen{startResult.mail_error ? `: ${startResult.mail_error}` : ''}</p>
                <p>Fallback — manuell weitergeben:</p>
                <div className="bg-white/10 rounded p-2 mt-1 font-mono text-[11px]">
                  Login: {startResult.portal_url}<br/>
                  Email: {startResult.customer.email}<br/>
                  Passwort: <span className="text-[#befa0a]">{startResult.initial_password}</span>
                </div>
                <button onClick={() => copyToClipboard(`Login: ${startResult.portal_url}\nEmail: ${startResult.customer.email}\nPasswort: ${startResult.initial_password}`)}
                  className="text-[10px] uppercase tracking-wider border border-white/30 px-2 py-1 rounded hover:bg-white/10 mt-1"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {copied ? '✓ Kopiert' : 'Zugangsdaten kopieren'}
                </button>
              </>
            ) : (
              <p>Bestehender Kunde wiederverwendet — keine neue Mail nötig.</p>
            )}
            <p className="text-gray-400 mt-2">{startResult.milestones_count} Termine · {startResult.sessions_count} Sessions angelegt</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Modul-2-Panel: Audit-Report generieren, ansehen, exportieren
function Module2Panel({ leadId, readonly }) {
  const [report, setReport] = useState(null);
  const [hasMetrics, setHasMetrics] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(60);
  const [generating, setGenerating] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/crm/leads/${leadId}/audit`, { headers: authHeaders() })
      .then(({ data }) => setReport(data && data.id ? data : null))
      .catch(() => setReport(null));
    axios.get(`${API}/crm/leads/${leadId}/metrics`, { headers: authHeaders() })
      .then(({ data }) => setHasMetrics((data?.metrics || []).length > 0))
      .catch(() => setHasMetrics(false));
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (report && !window.confirm('Bestehenden Audit-Report überschreiben?')) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/crm/leads/${leadId}/audit/generate`,
        { hourly_rate_eur: Number(hourlyRate) }, { headers: authHeaders() });
      setReport({ ...data, content_md: data.content_md, roi_summary_json: data.roi_summary });
    } catch (e) { alert(e.response?.data?.detail || 'Fehler beim Generieren'); }
    setGenerating(false);
  };

  const downloadMd = () => {
    window.open(`${API}/crm/leads/${leadId}/audit/markdown`, '_blank');
  };

  return (
    <div className="mb-3 space-y-2">
      {!hasMetrics && !report && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[11px] text-amber-800">
          Noch keine Metriken erhoben — erst <strong>Modul 1 (AI Ready Check)</strong> mit dem Kunden durchführen.
        </div>
      )}

      {hasMetrics && !readonly && (
        <div className="bg-gray-50 border border-gray-200 rounded p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {report ? 'Report neu generieren' : 'Audit-Report generieren'}
          </p>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-gray-500">Stundensatz</label>
            <input type="number" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)}
              className="w-16 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:border-[#befa0a]" />
            <span className="text-[11px] text-gray-500">€/h</span>
            <button onClick={generate} disabled={generating}
              className="ml-auto text-xs bg-[#0a0a0a] text-[#befa0a] px-3 py-1 rounded font-bold uppercase tracking-wider hover:bg-[#1f1f1f] disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {generating ? 'Generiert…' : (report ? 'Neu generieren' : 'Audit erstellen')}
            </button>
          </div>
        </div>
      )}

      {report && report.content_md && (
        <div className="bg-white border border-gray-200 rounded p-2.5">
          {/* ROI-Zusammenfassung als Hero */}
          {report.roi_summary_json && (
            <div className="grid grid-cols-3 gap-2 mb-2 pb-2 border-b border-gray-100">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Min/Tag verloren</p>
                <p className="text-lg font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {report.roi_summary_json.total_minutes_per_day}
                </p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Stunden/Jahr</p>
                <p className="text-lg font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {report.roi_summary_json.annual_hours_saved}
                </p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">€/Jahr Potenzial</p>
                <p className="text-lg font-bold text-emerald-600" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {Number(report.roi_summary_json.annual_savings_eur).toLocaleString('de-DE')}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-wider text-[#658a07] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Audit-Report ({report.status === 'final' ? 'Final' : 'Entwurf'})
            </p>
            <div className="flex gap-1.5">
              <button onClick={() => setShowFull(s => !s)}
                className="text-[10px] uppercase tracking-wider text-gray-600 border border-gray-300 px-2 py-0.5 rounded hover:border-[#befa0a] font-bold"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {showFull ? 'Einklappen' : 'Volltext'}
              </button>
              <button onClick={downloadMd}
                className="text-[10px] uppercase tracking-wider bg-[#0a0a0a] text-[#befa0a] px-2 py-0.5 rounded font-bold"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                .md
              </button>
            </div>
          </div>
          <div className={`text-xs text-gray-700 whitespace-pre-wrap leading-relaxed ${showFull ? '' : 'max-h-40 overflow-hidden relative'}`}
            style={{ fontFamily: "'Manrope', sans-serif" }}>
            {report.content_md}
            {!showFull && (
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerProjectModules({ leadId, readonly }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/crm/leads/${leadId}/project`, { headers: authHeaders() })
      .then(({ data }) => setProject(data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [leadId]);

  const patch = async (patchObj, key) => {
    setSavingKey(key || 'x');
    try {
      const { data } = await axios.patch(`${API}/crm/leads/${leadId}/project`, patchObj, { headers: authHeaders() });
      setProject(data);
    } catch (e) { alert(e.response?.data?.detail || 'Fehler beim Speichern'); }
    setSavingKey(null);
  };

  if (loading) return <div className="mb-4 text-xs text-gray-400">Lädt Projekt-Status…</div>;
  if (!project) return null;

  const doneCount = PROJECT_MODULES.filter(m => project[`${m.key}_status`] === 'fertig').length;

  return (
    <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-[#0a0a0a] text-white px-4 py-2.5 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
          EnBet-Projekt · 3 Module
        </p>
        <span className="text-[10px] text-[#befa0a] font-bold">{doneCount}/3 fertig</span>
      </div>
      <div className="divide-y divide-gray-100">
        {PROJECT_MODULES.map(m => {
          const status = project[`${m.key}_status`] || 'offen';
          const meta = MODULE_STATUS_META[status] || MODULE_STATUS_META.offen;
          const isOpen = expanded === m.key;
          const notes = project[`${m.key}_notes`] || '';
          return (
            <div key={m.key} className="bg-white">
              <div className="px-3 py-2.5 flex items-center gap-3">
                <span className="w-6 h-6 flex-shrink-0 rounded-full bg-[#0a0a0a] text-[#befa0a] text-xs font-bold flex items-center justify-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {m.num}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#1f1f1f] truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>{m.label}</p>
                  <p className="text-[10px] text-gray-400 truncate">{m.sub}</p>
                </div>
                {readonly ? (
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${meta.cls}`}>{meta.label}</span>
                ) : (
                  <select value={status} disabled={savingKey === m.key}
                    onChange={e => patch({ [`${m.key}_status`]: e.target.value }, m.key)}
                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border-0 cursor-pointer ${meta.cls}`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <option value="offen">Gebucht</option>
                    <option value="laeuft">Im Prozess</option>
                    <option value="fertig">Abgeschlossen</option>
                  </select>
                )}
                <button onClick={() => setExpanded(isOpen ? null : m.key)} className="text-gray-400 hover:text-gray-700 p-1">
                  {isOpen ? <ChevronRight size={14} className="rotate-90 transition-transform" /> : <ChevronRight size={14} />}
                </button>
              </div>
              {isOpen && (
                <div className="px-3 pb-3 pt-1">
                  {m.key === 'module1' && <Module1Panel leadId={leadId} readonly={readonly} />}
                  {m.key === 'module2' && <Module2Panel leadId={leadId} readonly={readonly} />}
                  <textarea
                    defaultValue={notes}
                    readOnly={readonly}
                    onBlur={readonly ? undefined : (e) => { if (e.target.value !== notes) patch({ [`${m.key}_notes`]: e.target.value }, m.key); }}
                    placeholder={readonly ? 'Keine Notizen' : 'Notizen / Ergebnisse zu diesem Modul…'}
                    rows={3}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#befa0a]"
                    style={{ fontFamily: "'Manrope', sans-serif" }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Lead Detail Drawer ───────────────────────────────────────────────────────
function LeadDrawer({ lead, onClose, onUpdate, onDelete, currentUser }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...lead });
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [handoverEditing, setHandoverEditing] = useState(false);
  const [handoverText, setHandoverText] = useState(lead.customer_handover_report || '');
  const [handoverBusy, setHandoverBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ contact_role: 'eigentuemer', name: '', email: '', phone: '', position: '' });

  const role = currentUser?.role || 'admin';
  const isAdmin = role === 'admin';
  const isSales = role === 'sales';
  const isCustomerMgr = role === 'customer_manager';
  const isCustomer = (lead.kanban_status || 'neu') === 'kunde';
  const readonly = !!lead._readonly;

  // Load customer contacts when viewing a customer
  useEffect(() => {
    if (!isCustomer) return;
    axios.get(`${API}/crm/leads/${lead.id}/contacts`, { headers: authHeaders() })
      .then(({ data }) => setContacts(data || []))
      .catch(() => setContacts([]));
  }, [isCustomer, lead.id]);

  const regenerateHandover = async () => {
    if (!window.confirm('Neuen Übergabe-Bericht generieren? Dadurch wird der aktuelle überschrieben.')) return;
    setHandoverBusy(true);
    try {
      const { data } = await axios.post(`${API}/crm/leads/${lead.id}/handover/regenerate`, {}, { headers: authHeaders() });
      setHandoverText(data.report || '');
      onUpdate({ ...lead, customer_handover_report: data.report, customer_handover_approved: false });
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
    setHandoverBusy(false);
  };

  const saveHandover = async () => {
    setHandoverBusy(true);
    try {
      await axios.patch(`${API}/crm/leads/${lead.id}/handover`, { report: handoverText }, { headers: authHeaders() });
      onUpdate({ ...lead, customer_handover_report: handoverText, customer_handover_approved: false });
      setHandoverEditing(false);
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
    setHandoverBusy(false);
  };

  const approveHandover = async () => {
    if (!window.confirm('Übergabe-Bericht freigeben? Customer-Manager sieht ihn dann.')) return;
    setHandoverBusy(true);
    try {
      await axios.post(`${API}/crm/leads/${lead.id}/handover/approve`, {}, { headers: authHeaders() });
      onUpdate({ ...lead, customer_handover_approved: true });
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
    setHandoverBusy(false);
  };

  const addContact = async () => {
    if (!newContact.name && !newContact.email) return;
    try {
      const { data } = await axios.post(`${API}/crm/contacts`, { ...newContact, lead_id: lead.id }, { headers: authHeaders() });
      setContacts(prev => [...prev, data]);
      setNewContact({ contact_role: 'eigentuemer', name: '', email: '', phone: '', position: '' });
      setShowNewContact(false);
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Kontakt löschen?')) return;
    try {
      await axios.delete(`${API}/crm/contacts/${id}`, { headers: authHeaders() });
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
  };

  const CONTACT_ROLE_LABELS = {
    eigentuemer: 'Eigentümer',
    verwaltung: 'Verwaltung',
    management: 'Management',
    dsgvo: 'DSGVO-Beauftragter',
    it: 'IT-Verantwortlicher',
    daten: 'Daten-Verantwortlicher',
    rollout: 'Rollout-Manager',
    sonstiges: 'Sonstiges',
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/crm/leads/${lead.id}`, form, { headers: authHeaders() });
      onUpdate({ ...lead, ...form });
      setEditing(false);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/crm/leads/${lead.id}/interactions`, { type: 'note', content: noteText }, { headers: authHeaders() });
      setNoteText('');
      onUpdate(null); // trigger reload
    } catch (e) { console.error(e); }
  };

  const del = async () => {
    if (!window.confirm('Lead wirklich löschen?')) return;
    await axios.delete(`${API}/crm/leads/${lead.id}`, { headers: authHeaders() });
    onDelete(lead.id);
    onClose();
  };

  const unsubscribe = async () => {
    if (!window.confirm(`${lead.company_name || 'Lead'} als unsubscribed markieren? Felix und Lea senden dann keine Mails mehr an diesen Lead.`)) return;
    try {
      await axios.post(`${API}/crm/leads/${lead.id}/unsubscribe`, {}, { headers: authHeaders() });
      onUpdate({ ...lead, unsubscribed_at: new Date().toISOString() });
    } catch (e) { alert(e.response?.data?.detail || 'Fehler beim Markieren'); }
  };

  const resubscribe = async () => {
    try {
      await axios.post(`${API}/crm/leads/${lead.id}/resubscribe`, {}, { headers: authHeaders() });
      onUpdate({ ...lead, unsubscribed_at: null });
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
  };

  const field = (label, key, type = 'text') => (
    <div className="mb-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{label}</p>
      {editing ? (
        <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-[#1f1f1f] focus:outline-none focus:border-[#befa0a]"
          style={{ fontFamily: "'Manrope', sans-serif" }} />
      ) : (
        <p className="text-sm text-[#1f1f1f]" style={{ fontFamily: "'Manrope', sans-serif" }}>{lead[key] || '—'}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="hidden md:block flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full md:max-w-lg bg-white h-full overflow-y-auto flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-[#1f1f1f] uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {lead.company_name}
            </h2>
            <p className="text-xs text-gray-400">{formatDate(lead.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <button onClick={save} disabled={saving} className="bg-[#befa0a] text-[#0a0a0a] px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-lime-300 transition-colors flex items-center gap-1">
                <Check size={13} /> Speichern
              </button>
            ) : (
              <button onClick={() => setEditing(true)} className="border border-gray-200 text-gray-500 px-3 py-1.5 text-xs rounded hover:border-gray-400 transition-colors flex items-center gap-1">
                <Edit3 size={13} /> Bearbeiten
              </button>
            )}
            <button onClick={del} className="text-red-400 hover:text-red-600 p-1.5 transition-colors"><Trash2 size={16} /></button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5 transition-colors"><X size={18} /></button>
          </div>
        </div>

        <div className="px-5 py-4 flex-1">
          {/* Unsubscribed Warning */}
          {lead.unsubscribed_at && (
            <div className="mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-2">
              <div className="text-xs text-amber-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <strong>⚠ Unsubscribed</strong> — Felix/Lea senden keine Mails mehr.
              </div>
              <button onClick={resubscribe} className="text-[10px] uppercase font-bold text-amber-700 hover:text-amber-900 border border-amber-300 px-2 py-1 rounded" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Rückgängig
              </button>
            </div>
          )}

          {/* Read-only Banner */}
          {readonly && (
            <div className="mb-4 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              👁 Read-Only · Du hast keine Edit-Rechte für diesen Lead.
            </div>
          )}

          {/* EnBet 3-Modul-Projekt (nur bei Kunden) */}
          {isCustomer && <CustomerProjectModules leadId={lead.id} readonly={readonly || isCustomerMgr} />}

          {/* Handover-Report Block (nur bei Kunden) */}
          {isCustomer && (
            <div className="mb-4">
              {/* Customer-Manager: nur freigegebenen Bericht sehen */}
              {isCustomerMgr && (
                lead.customer_handover_approved && lead.customer_handover_report ? (
                  <div className="border-2 border-[#befa0a] bg-[#befa0a]/5 rounded-lg p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#658a07] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      📋 Übergabe-Bericht von Sales
                    </p>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {lead.customer_handover_report}
                    </div>
                  </div>
                ) : (
                  <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Übergabe-Bericht von Sales noch nicht freigegeben.
                  </div>
                )
              )}

              {/* Sales / Admin: Editor + Approve */}
              {(isSales || isAdmin) && (
                <div className={`border-2 rounded-lg p-4 ${lead.customer_handover_approved ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/40'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {lead.customer_handover_approved ? '✓ Übergabe freigegeben' : '⏳ Übergabe-Bericht prüfen + freigeben'}
                    </p>
                    {!handoverEditing && !lead.customer_handover_report && (
                      <button onClick={regenerateHandover} disabled={handoverBusy}
                        className="text-xs text-[#658a07] border border-[#658a07] px-2 py-0.5 rounded hover:bg-[#658a07] hover:text-white disabled:opacity-50">
                        {handoverBusy ? 'Generiert...' : 'Generieren'}
                      </button>
                    )}
                  </div>
                  {handoverEditing ? (
                    <>
                      <textarea value={handoverText} onChange={e => setHandoverText(e.target.value)} rows={12}
                        className="w-full border border-gray-300 rounded p-2 text-sm font-mono"
                        style={{ fontFamily: "'Manrope', sans-serif" }} />
                      <div className="flex gap-2 mt-2">
                        <button onClick={saveHandover} disabled={handoverBusy}
                          className="bg-[#0a0a0a] text-white px-3 py-1.5 text-xs font-bold uppercase rounded">Speichern</button>
                        <button onClick={() => { setHandoverEditing(false); setHandoverText(lead.customer_handover_report || ''); }}
                          className="text-gray-500 text-xs px-2">Abbrechen</button>
                      </div>
                    </>
                  ) : lead.customer_handover_report ? (
                    <>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        {lead.customer_handover_report}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setHandoverEditing(true)}
                          className="text-xs border border-gray-300 px-2 py-1 rounded hover:border-gray-500">Bearbeiten</button>
                        <button onClick={regenerateHandover} disabled={handoverBusy}
                          className="text-xs border border-gray-300 px-2 py-1 rounded hover:border-gray-500 disabled:opacity-50">Neu generieren</button>
                        {!lead.customer_handover_approved && (
                          <button onClick={approveHandover} disabled={handoverBusy}
                            className="text-xs bg-[#befa0a] text-[#0a0a0a] font-bold uppercase px-2 py-1 rounded hover:bg-lime-300 disabled:opacity-50"
                            style={{ fontFamily: "'Oswald', sans-serif" }}>
                            ✓ Freigeben
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Noch kein Bericht. Klick "Generieren" für Auto-Draft.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customer-Contacts (mehrere Verantwortliche) — nur bei Kunden */}
          {isCustomer && !readonly && (
            <div className="mb-4 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Verantwortliche
                </p>
                <button onClick={() => setShowNewContact(s => !s)}
                  className="text-xs text-[#658a07] hover:underline">
                  + Hinzufügen
                </button>
              </div>
              {contacts.length === 0 && !showNewContact && (
                <p className="text-xs text-gray-400" style={{ fontFamily: "'Manrope', sans-serif" }}>Noch keine Verantwortlichen erfasst.</p>
              )}
              <div className="space-y-1.5 mb-2">
                {contacts.map(c => (
                  <div key={c.id} className="border border-gray-100 rounded p-2 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    <div className="flex items-center justify-between">
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-bold uppercase">{CONTACT_ROLE_LABELS[c.contact_role] || c.contact_role}</span>
                      <button onClick={() => deleteContact(c.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={11} /></button>
                    </div>
                    {c.name && <p className="font-bold mt-1 text-[#1f1f1f]">{c.name}{c.position && <span className="text-gray-500 font-normal"> · {c.position}</span>}</p>}
                    {c.email && <a href={`mailto:${c.email}`} className="text-[#658a07] hover:underline block">{c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone}`} className="text-gray-600 block">{c.phone}</a>}
                    {c.notes && <p className="text-gray-500 italic mt-1">{c.notes}</p>}
                  </div>
                ))}
              </div>
              {showNewContact && (
                <div className="border border-[#befa0a]/40 bg-[#befa0a]/5 rounded p-2 space-y-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <select value={newContact.contact_role} onChange={e => setNewContact(c => ({ ...c, contact_role: e.target.value }))}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs">
                    {Object.entries(CONTACT_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input value={newContact.name} onChange={e => setNewContact(c => ({ ...c, name: e.target.value }))} placeholder="Name"
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" />
                  <input value={newContact.position} onChange={e => setNewContact(c => ({ ...c, position: e.target.value }))} placeholder="Position (z.B. CTO)"
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" />
                  <input value={newContact.email} onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))} placeholder="E-Mail"
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" />
                  <input value={newContact.phone} onChange={e => setNewContact(c => ({ ...c, phone: e.target.value }))} placeholder="Telefon"
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" />
                  <div className="flex gap-2">
                    <button onClick={addContact} disabled={!newContact.name && !newContact.email}
                      className="bg-[#0a0a0a] text-white px-3 py-1 text-xs font-bold uppercase rounded disabled:opacity-40">Anlegen</button>
                    <button onClick={() => setShowNewContact(false)} className="text-gray-500 text-xs">Abbrechen</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status + Score */}
          <div className="flex gap-3 mb-5">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Status</p>
              {editing ? (
                <select value={form.kanban_status || 'neu'} onChange={e => setForm(f => ({ ...f, kanban_status: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]">
                  {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              ) : (
                <span className="text-sm font-bold" style={{ color: COLUMNS.find(c => c.id === lead.kanban_status)?.color || '#6b7280' }}>
                  {COLUMNS.find(c => c.id === lead.kanban_status)?.label || 'Neu'}
                </span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Score</p>
              {editing ? (
                <select value={form.score || 'unknown'} onChange={e => setForm(f => ({ ...f, score: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]">
                  {['hot','warm','cold','unknown'].map(s => <option key={s} value={s}>{SCORE_LABELS[s]}</option>)}
                </select>
              ) : (
                <span className="text-sm font-bold" style={{ color: SCORE_COLORS[lead.score] }}>{SCORE_LABELS[lead.score]}</span>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            {field('Vorname', 'contact_name')}
            {field('Nachname', 'contact_lastname')}
            {field('E-Mail', 'contact_email', 'email')}
            {field('Telefon', 'contact_phone', 'tel')}
            {field('Unternehmen', 'company_name')}
            {field('Branche', 'industry')}
            {field('Standorte', 'num_locations', 'number')}
            {field('Website', 'website')}
            {field('LinkedIn', 'linkedin_url')}
          </div>

          {/* Pain Points */}
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Pain Points</p>
            {editing ? (
              <textarea value={form.pain_points || ''} onChange={e => setForm(f => ({ ...f, pain_points: e.target.value }))} rows={3}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-[#1f1f1f] focus:outline-none focus:border-[#befa0a] resize-none"
                style={{ fontFamily: "'Manrope', sans-serif" }} />
            ) : (
              <p className="text-sm text-[#1f1f1f]" style={{ fontFamily: "'Manrope', sans-serif" }}>{lead.pain_points || '—'}</p>
            )}
          </div>

          {/* Notes */}
          <div className="mb-5">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Notizen</p>
            {editing ? (
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-[#1f1f1f] focus:outline-none focus:border-[#befa0a] resize-none"
                style={{ fontFamily: "'Manrope', sans-serif" }} />
            ) : (
              <p className="text-sm text-[#1f1f1f]" style={{ fontFamily: "'Manrope', sans-serif" }}>{lead.notes || '—'}</p>
            )}
          </div>

          {/* Pending Drafts */}
          {(lead.interactions || []).filter(i => i.type === 'email_draft' && !i.sent_at).map(d => (
            <DraftEmailCard key={d.id} draft={d}
              onSent={() => onUpdate(null)}
              onDiscard={() => onUpdate(null)} />
          ))}

          {/* Timeline */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>Timeline</p>
            <div className="space-y-2 mb-3">
              {(lead.interactions || []).filter(i => !(i.type === 'email_draft' && !i.sent_at)).map((ia, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5 text-gray-400">
                    {INTERACTION_ICONS[ia.type] || <ChevronRight size={13} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#1f1f1f] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {ia.type === 'email' && ia.subject ? <strong>{ia.subject}<br/></strong> : null}
                      {ia.content}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(ia.sent_at || ia.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Book Meeting */}
            <button onClick={() => setShowBooking(true)}
              className="w-full flex items-center justify-center gap-2 border-2 border-[#befa0a] text-[#1f1f1f] py-2.5 rounded font-bold uppercase tracking-wider text-sm hover:bg-[#befa0a] transition-colors mb-3"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Clock size={15} /> Termin buchen
            </button>
            {/* Dokumente (Angebote + Rechnungen) */}
            <LeadDocumentsSection lead={lead} customerId={lead.customer_id} />

            {/* Add Note */}
            <div className="flex gap-2 mt-4">
              <input value={noteText} onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addNote(); }}
                placeholder="Notiz oder Aktion hinzufügen..."
                className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]"
                style={{ fontFamily: "'Manrope', sans-serif" }} />
              <button onClick={addNote} className="bg-[#befa0a] text-[#0a0a0a] px-3 py-2 rounded text-sm font-bold hover:bg-lime-300 transition-colors">
                <Plus size={15} />
              </button>
            </div>

            {/* Unsubscribe-Aktion (nur wenn aktiv) */}
            {!lead.unsubscribed_at && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button onClick={unsubscribe}
                  className="w-full text-xs text-amber-700 hover:text-amber-900 border border-amber-200 hover:border-amber-300 py-2 rounded transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Als unsubscribed markieren (Felix + Lea stoppen)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Termin-Buchen-Modal direkt im Drawer mounten — der Parent-Mount würde
          den lokalen showBooking-State nicht lesen und der Klick wäre tot. */}
      {showBooking && (
        <BookMeetingModal
          lead={lead}
          onClose={() => setShowBooking(false)}
          onBooked={(data) => {
            setShowBooking(false);
            if (onUpdate && data?.lead) onUpdate(data.lead);
          }}
        />
      )}
    </div>
  );
}

// ── New Lead Modal ───────────────────────────────────────────────────────────
function NewLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ company_name: '', contact_name: '', contact_email: '', source: 'manual', kanban_status: 'neu', score: 'unknown' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.company_name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/crm/leads`, form, { headers: authHeaders() });
      onCreated(data);
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Neuer Lead</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {[['Unternehmen *', 'company_name'], ['Ansprechpartner', 'contact_name'], ['E-Mail', 'contact_email'], ['Branche', 'industry']].map(([label, key]) => (
          <div key={key} className="mb-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</p>
            <input value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]"
              style={{ fontFamily: "'Manrope', sans-serif" }} />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Quelle</p>
            <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
              className="w-full border border-gray-200 rounded px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              {['manual','website-chatbot','linkedin','email','empfehlung','event'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Score</p>
            <select value={form.score} onChange={e => setForm(f => ({ ...f, score: e.target.value }))}
              className="w-full border border-gray-200 rounded px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              {['hot','warm','cold','unknown'].map(s => <option key={s} value={s}>{SCORE_LABELS[s]}</option>)}
            </select>
          </div>
        </div>
        <button onClick={submit} disabled={saving || !form.company_name.trim()}
          className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-40"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          {saving ? 'Wird erstellt...' : 'Lead erstellen'}
        </button>
      </div>
    </div>
  );
}

// ── Password Field with show/hide ────────────────────────────────────────────
function PasswordField({ label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</p>
      <div className="relative">
        <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} required
          className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] pr-10"
          style={{ fontFamily: "'Manrope', sans-serif" }} />
        <button type="button" onClick={() => setShow(s => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
    </div>
  );
}

// ── Sidebar Navigation ───────────────────────────────────────────────────────
function Sidebar({ activeView, onNavigate, onOpenModal, onLogout, mobileOpen, onMobileClose, user }) {
  const role = user?.role || 'admin';
  const isAdmin = role === 'admin';
  const isSales = role === 'sales';
  const isCustomerMgr = role === 'customer_manager';

  // Build items based on role
  const all = [
    { id: 'heute', label: 'Heute', icon: Home, type: 'view', roles: ['admin', 'sales', 'customer_manager'] },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare, type: 'view', roles: ['admin', 'sales', 'customer_manager'] },
    { id: 'johanna', label: 'Johanna', icon: MessageSquare, type: 'link', href: '/johanna', roles: ['admin', 'sales', 'customer_manager'] },
    null,
    { type: 'header', label: 'Pipeline', roles: ['admin', 'sales', 'customer_manager'] },
    { id: 'leads', label: 'Leads', icon: Users, type: 'view', roles: ['admin', 'sales'] },
    { id: 'kunden', label: 'Kunden', icon: Star, type: 'view', roles: ['admin', 'sales', 'customer_manager'] },
    null,
    { type: 'header', label: 'Agents', roles: ['admin', 'sales'] },
    { id: 'felix', label: 'Felix · Outbound', icon: Send, type: 'view', roles: ['admin', 'sales'] },
    { id: 'lea', label: 'Lea · Sales', icon: Zap, type: 'view', roles: ['admin', 'sales'] },
    { id: 'research', label: 'Research', icon: Search, type: 'view', roles: ['admin', 'sales'] },
    { id: 'content', label: 'Content', icon: TrendingUp, type: 'view', roles: ['admin'] },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, type: 'view', roles: ['admin'] },
    { id: 'masterclass', label: 'Masterclass', icon: GraduationCap, type: 'view', roles: ['admin'] },
    { id: 'orders', label: 'Bestellungen', icon: CreditCard, type: 'view', roles: ['admin'] },
    { id: 'readychecks', label: 'Ready Checks', icon: ClipboardCheck, type: 'view', roles: ['admin'] },
    null,
    { type: 'header', label: 'Newsletter', roles: ['admin'] },
    { id: 'newsletter', label: 'Komponieren', icon: Newspaper, type: 'view', roles: ['admin'] },
    { id: 'subscribers', label: 'Abonnenten', icon: Mail, type: 'view', roles: ['admin'] },
    { id: 'history', label: 'Historie', icon: History, type: 'view', roles: ['admin'] },
  ];

  const items = all.filter(it => !it || (it.roles && it.roles.includes(role)));

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={onMobileClose} />}
      {/* Mobile: position fixed (out of flow, kein Platz reserviert).
          Desktop: position sticky (im flex flow nebem dem main).
          WICHTIG: keine 'sticky' Base-Klasse — sonst überschreibt sie fixed auf Mobile. */}
      <aside className={`bg-[#0a0a0a] text-white w-60 flex flex-col h-screen z-50 transition-transform fixed inset-y-0 left-0 md:sticky md:top-0 md:z-auto md:flex-shrink-0 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
          <span className="text-[#befa0a] font-bold uppercase tracking-wider text-sm" style={{ fontFamily: "'Oswald', sans-serif" }}>
            ENBET · CRM
          </span>
          <button onClick={onMobileClose} className="md:hidden text-white/40"><X size={18} /></button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3">
          {items.map((item, i) => {
            if (!item) return <div key={i} className="h-3" />;
            if (item.type === 'header') return (
              <p key={i} className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white/30" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.label}
              </p>
            );
            const Icon = item.icon;
            if (item.type === 'link') {
              return (
                <a key={i} href={item.href}
                  className="w-full px-5 py-2.5 text-left flex items-center gap-3 transition-colors text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent">
                  <Icon size={16} />
                  <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>{item.label}</span>
                </a>
              );
            }
            const active = activeView === item.id;
            return (
              <button key={i} onClick={() => onNavigate(item.id)}
                className={`w-full px-5 py-2.5 text-left flex items-center gap-3 transition-colors ${
                  active ? 'bg-[#befa0a]/10 text-[#befa0a] border-l-2 border-[#befa0a]' : 'text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                }`}>
                <Icon size={16} />
                <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer: User */}
        <div className="border-t border-white/10 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {user?.name || user?.email || 'User'}
              </p>
              <p className="text-[10px] text-white/40 truncate">{user?.email || ''}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => onOpenModal('agentSettings')} title="Agent" className="text-white/40 hover:text-white p-1.5"><Sparkles size={14} /></button>
              <button onClick={() => onOpenModal('users')} title="Nutzer" className="text-white/40 hover:text-white p-1.5"><Users size={14} /></button>
              <button onClick={() => onOpenModal('settings')} title="Passwort" className="text-white/40 hover:text-white p-1.5"><Settings size={14} /></button>
              <button onClick={onLogout} title="Logout" className="text-white/40 hover:text-red-400 p-1.5"><LogOut size={14} /></button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

// ── Dashboard "Heute" View ───────────────────────────────────────────────────
function DashboardToday({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [metricsDays, setMetricsDays] = useState(30);
  const [tasksSummary, setTasksSummary] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [siteStats, setSiteStats] = useState(null);
  const [myDay, setMyDay] = useState(null);

  const loadTasks = () => {
    axios.get(`${API}/tasks/summary`, { headers: authHeaders() })
      .then(({ data }) => setTasksSummary(data))
      .catch(() => {});
    axios.get(`${API}/tasks?today=true&status=open,in_progress,waiting`, { headers: authHeaders() })
      .then(({ data }) => setTodayTasks(data))
      .catch(() => {});
    axios.get(`${API}/my-day`, { headers: authHeaders() })
      .then(({ data }) => setMyDay(data))
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/dashboard/today`, { headers: authHeaders() })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    loadTasks();
    axios.get(`${API}/analytics/stats`, { headers: authHeaders() })
      .then(({ data }) => setSiteStats(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/agents/metrics/summary?days=${metricsDays}`, { headers: authHeaders() })
      .then(({ data }) => setMetrics(data))
      .catch(() => {});
  }, [metricsDays]);

  const completeTask = async (id) => {
    await axios.post(`${API}/tasks/${id}/complete`, {}, { headers: authHeaders() });
    loadTasks();
  };

  if (loading) return <div className="p-8 text-gray-400">Lädt...</div>;
  if (!data) return <div className="p-8 text-gray-400">Keine Daten</div>;

  const today = new Date(data.now);
  const dateStr = today.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="p-4 md:p-10 max-w-6xl mx-auto">
      <div className="mb-6 md:mb-10">
        <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Heute
        </p>
        <h1 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Hallo {data.user_name}
        </h1>
        <p className="text-gray-500 mt-1 text-sm md:text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>{dateStr}</p>
      </div>

      {/* Mein Tag — kombinierte Timeline (immer sichtbar, auch wenn leer) */}
      {myDay && (
        <div className="bg-[#0a0a0a] text-white rounded-xl p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-[#befa0a]" />
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Mein Tag
              </h2>
              <span className="text-[10px] px-2 py-0.5 bg-[#befa0a]/20 text-[#befa0a] rounded font-bold uppercase">
                {myDay.totals.tasks_count} Tasks · {myDay.totals.posts_count} Posts
              </span>
            </div>
            <div className="text-xs text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {(() => {
                const h = Math.floor(myDay.totals.total_minutes / 60);
                const m = myDay.totals.total_minutes % 60;
                return h > 0 ? `${h}h ${m}min geplant` : `${m} Min geplant`;
              })()}
            </div>
          </div>
          {myDay.items?.length === 0 ? (
            <div className="py-6 text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <p className="text-white/70 text-sm">Heute nichts geplant 🎉</p>
              <p className="text-white/40 text-xs mt-1">
                Keine fälligen Tasks und keine geplanten Posts für heute.
              </p>
              <button onClick={() => onNavigate('tasks')}
                className="mt-3 text-xs text-[#befa0a] hover:text-white uppercase tracking-wider font-bold"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                + Task anlegen
              </button>
            </div>
          ) : (
          <div className="space-y-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {myDay.items.map(it => (
              <div key={`${it.type}-${it.id}`} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                <span className="text-[#befa0a] font-bold text-xs tabular-nums w-12 flex-shrink-0" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {it.time_local}
                </span>
                <span className="text-base flex-shrink-0">
                  {it.type === 'task' ? '📝' : (it.platform === 'linkedin' ? '💼' : '📸')}
                </span>
                <span className="text-sm flex-1 truncate text-white/90">
                  {it.title}
                </span>
                {it.estimated_minutes > 0 && (
                  <span className="text-[11px] text-white/40 flex-shrink-0">
                    {it.estimated_minutes >= 60 ? `${(it.estimated_minutes/60).toFixed(it.estimated_minutes % 60 ? 1 : 0)}h` : `${it.estimated_minutes}min`}
                  </span>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Tasks-Heute Card (prominent oben) */}
      {(tasksSummary && (tasksSummary.due_today > 0 || tasksSummary.overdue > 0)) && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckSquare size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Tasks
              </h2>
              {tasksSummary.overdue > 0 && (
                <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded font-bold uppercase">
                  {tasksSummary.overdue} überfällig
                </span>
              )}
            </div>
            <button onClick={() => onNavigate('tasks')} className="text-xs text-[#658a07] hover:underline" style={{ fontFamily: "'Manrope', sans-serif" }}>
              alle öffnen →
            </button>
          </div>
          {todayTasks.length === 0 && tasksSummary.overdue === 0 ? (
            <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>Heute nichts zu erledigen 🎉</p>
          ) : (
            <div className="space-y-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {todayTasks.slice(0, 5).map(t => {
                const due = t.due_at ? new Date(t.due_at) : null;
                const overdue = due && due < new Date();
                return (
                  <div key={t.id} className={`flex items-start gap-2 p-2 rounded ${overdue ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                    <button onClick={() => completeTask(t.id)}
                      className="mt-0.5 w-5 h-5 rounded border-2 border-gray-300 hover:border-[#befa0a] flex-shrink-0 flex items-center justify-center transition-colors">
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1f1f1f] truncate">{t.title}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[11px]">
                        {due && (
                          <span className={overdue ? 'text-red-600 font-bold' : 'text-gray-500'}>
                            {overdue ? '⚠ ' : ''}{due.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {t.assignee_label && <span className="text-gray-500">→ {t.assignee_label}</span>}
                        {t.lead && <span className="text-[#658a07] font-medium">· {t.lead.company_name || 'Lead'}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {todayTasks.length > 5 && (
                <button onClick={() => onNavigate('tasks')} className="text-xs text-[#658a07] hover:underline mt-2">
                  + {todayTasks.length - 5} weitere ansehen
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Upcoming Meetings */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Kommende Termine
              </h2>
            </div>
            <span className="text-xs text-gray-400">{data.upcoming_meetings.length}</span>
          </div>
          {data.upcoming_meetings.length === 0 ? (
            <p className="text-sm text-gray-400" style={{ fontFamily: "'Manrope', sans-serif" }}>Keine Termine in den nächsten 7 Tagen.</p>
          ) : (
            <div className="space-y-3">
              {data.upcoming_meetings.map((m, i) => {
                const dt = new Date(m.meeting_at);
                const dayLabel = m.is_today ? 'Heute' : dt.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
                const timeLabel = dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                return (
                  <button key={i} onClick={() => onNavigate('leads', m.lead_id)}
                    className="w-full text-left border-l-2 border-[#befa0a] pl-3 py-1 hover:bg-gray-50 transition-colors">
                    <div className="flex items-baseline gap-3">
                      <span className={`text-xs font-bold ${m.is_today ? 'text-[#658a07]' : 'text-gray-500'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {dayLabel} · {timeLabel}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {m.company || 'Unbekannt'}
                    </p>
                    {m.contact && <p className="text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>{m.contact}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Lead Stats */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Pipeline
              </h2>
            </div>
            <button onClick={() => onNavigate('leads')} className="text-xs text-[#658a07] hover:underline">öffnen →</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat label="Aktiv" value={data.lead_stats.total_active} color="text-[#1f1f1f]" />
            <Stat label="🔥 Hot" value={data.lead_stats.hot} color="text-red-500" />
            <Stat label="Heute neu" value={data.lead_stats.today} color="text-[#658a07]" />
            <Stat label="Neu" value={data.lead_stats.neu} color="text-gray-600" />
            <Stat label="Termin" value={data.lead_stats.termin} color="text-purple-600" />
            <Stat label="Kunde" value={data.lead_stats.kunde} color="text-emerald-600" />
          </div>
        </div>

        {/* Lea Drafts */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Lea · Sales Inbox
              </h2>
            </div>
            <button onClick={() => onNavigate('lea')} className="text-xs text-[#658a07] hover:underline">öffnen →</button>
          </div>
          <p className="text-3xl font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {data.pending_drafts}
          </p>
          <p className="text-xs text-gray-500 uppercase mt-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Entwürfe wartend
          </p>
          {data.pending_drafts === 0 && (
            <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Klick "Lea ausführen" um neue Follow-ups zu erstellen.
            </p>
          )}
        </div>

        {/* Newsletter */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Newspaper size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Newsletter
              </h2>
            </div>
            <button onClick={() => onNavigate('subscribers')} className="text-xs text-[#658a07] hover:underline">öffnen →</button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Stat label="Abonnenten" value={data.newsletter.confirmed} color="text-[#1f1f1f]" />
            <Stat label="Ausstehend" value={data.newsletter.pending} color="text-amber-600" />
          </div>
          {data.last_campaign && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Letzter Versand: {new Date(data.last_campaign.sent_at).toLocaleDateString('de-DE')}
              </p>
              <p className="text-xs text-gray-400 truncate">"{data.last_campaign.subject}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Website-Besucher */}
      {siteStats && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mt-4 md:mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Website-Besucher
              </h2>
            </div>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Eigene Analytics
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Heute" value={siteStats.today_views || 0} color="text-[#658a07]" />
            <Stat label="7 Tage" value={siteStats.week_views || 0} color="text-[#1f1f1f]" />
            <Stat label="30 Tage" value={siteStats.month_views || 0} color="text-[#1f1f1f]" />
            <Stat label="Gesamt" value={siteStats.total_views || 0} color="text-gray-500" />
          </div>
          {/* Daily Bar Chart (7 Tage) */}
          {siteStats.daily && siteStats.daily.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Letzte 7 Tage
              </p>
              <div className="flex items-end gap-1 h-20">
                {(() => {
                  const maxVal = Math.max(1, ...siteStats.daily.map(d => d.views));
                  return siteStats.daily.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="flex-1 w-full flex items-end">
                        <div
                          className="w-full bg-[#befa0a] rounded-t transition-all min-h-[2px]"
                          style={{ height: `${(d.views / maxVal) * 100}%` }}
                          title={`${d.views} Views`}
                        />
                      </div>
                      <div className="text-[9px] text-gray-400 font-medium">{d.date}</div>
                      <div className="text-[10px] text-[#1f1f1f] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{d.views}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
          {/* Top Pages */}
          {siteStats.pages && siteStats.pages.length > 0 && (
            <div className="border-t border-gray-100 pt-3 mt-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Top Seiten (7 Tage)
              </p>
              <div className="space-y-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {siteStats.pages.slice(0, 5).map((p, i) => {
                  const maxV = siteStats.pages[0].views || 1;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-700 truncate">{p.page || '/'}</p>
                        <div className="h-1.5 bg-gray-100 rounded mt-0.5 overflow-hidden">
                          <div className="h-full bg-[#befa0a]" style={{ width: `${(p.views / maxV) * 100}%` }} />
                        </div>
                      </div>
                      <span className="text-xs font-bold text-[#1f1f1f] w-10 text-right" style={{ fontFamily: "'Oswald', sans-serif" }}>{p.views}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Agent-Performance */}
      {metrics && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mt-4 md:mt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Agent-Performance
              </h2>
            </div>
            <div className="flex gap-1">
              {[30, 90, 365].map(d => (
                <button key={d} onClick={() => setMetricsDays(d)}
                  className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${metricsDays === d ? 'bg-[#0a0a0a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {d}T
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Felix */}
            <div className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Send size={13} className="text-[#658a07]" />
                <p className="text-xs font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Felix · Outbound</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xl font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{metrics.agents?.outbound?.sent || 0}</p><p className="text-[10px] text-gray-500 uppercase">Sent</p></div>
                <div><p className="text-xl font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{metrics.rates?.felix_open_rate || 0}%</p><p className="text-[10px] text-gray-500 uppercase">Öffnung</p></div>
                <div><p className="text-xl font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{metrics.rates?.felix_reply_rate || 0}%</p><p className="text-[10px] text-gray-500 uppercase">Antwort</p></div>
              </div>
            </div>
            {/* Lea */}
            <div className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={13} className="text-[#658a07]" />
                <p className="text-xs font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Lea · Sales</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xl font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{metrics.agents?.sales?.sent || 0}</p><p className="text-[10px] text-gray-500 uppercase">Sent</p></div>
                <div><p className="text-xl font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{metrics.rates?.lea_open_rate || 0}%</p><p className="text-[10px] text-gray-500 uppercase">Öffnung</p></div>
                <div><p className="text-xl font-bold text-purple-600" style={{ fontFamily: "'Oswald', sans-serif" }}>{metrics.agents?.sales?.booked || 0}</p><p className="text-[10px] text-gray-500 uppercase">Termine</p></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p className={`text-2xl font-bold ${color}`} style={{ fontFamily: "'Oswald', sans-serif" }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>{label}</p>
    </div>
  );
}

// ── Newsletter History Modal ─────────────────────────────────────────────────
function NewsletterHistoryModal({ onClose, inline = false }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editing, setEditing] = useState(null); // campaign currently in edit-modal

  const reloadCampaigns = useCallback(() => {
    axios.get(`${API}/newsletter/campaigns`, { headers: authHeaders() })
      .then(({ data }) => setCampaigns(data))
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reloadCampaigns(); }, [reloadCampaigns]);

  const openDetail = async (c) => {
    setSelected(c);
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API}/newsletter/campaigns/${c.id}`, { headers: authHeaders() });
      setRecipients(data.recipients || []);
    } catch { setRecipients([]); }
    setLoadingDetail(false);
  };

  const unpublishCampaign = async (c, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${c.subject}" von der Website nehmen? Der Insights-Beitrag ist danach nicht mehr erreichbar.`)) return;
    try {
      await axios.patch(`${API}/newsletter/campaigns/${c.id}/unpublish`, {}, { headers: authHeaders() });
      reloadCampaigns();
    } catch (err) {
      alert(err.response?.data?.detail || 'Unpublish fehlgeschlagen');
    }
  };

  return (
    <Panel inline={inline} onClose={onClose} maxWidth="max-w-3xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-[#1f1f1f] uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {selected ? selected.subject : 'Newsletter-Historie'}
          </h2>
          <div className="flex items-center gap-2">
            {selected && (
              <button onClick={() => { setSelected(null); setRecipients([]); }}
                className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 px-3 py-1.5 rounded">
                ← Übersicht
              </button>
            )}
            {!inline && <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>}
          </div>
        </div>

        {!selected ? (
          <div className="px-6 py-4">
            {loading ? <p className="text-gray-400 text-sm">Lädt...</p> : campaigns.length === 0 ? (
              <p className="text-center text-gray-400 py-12 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Noch keine Newsletter versendet.
              </p>
            ) : (
              <div className="space-y-2">
                {campaigns.map(c => (
                  <div key={c.id}
                    className="w-full border border-gray-200 rounded-lg p-4 hover:border-[#befa0a] hover:bg-[#befa0a]/5 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <button onClick={() => openDetail(c)} className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                            {c.subject}
                          </p>
                          {c.published_on_website && (
                            <span className="text-[9px] uppercase tracking-wider bg-[#658a07] text-white px-1.5 py-0.5 rounded font-bold" title={`Live unter /insights/${c.slug}`}>
                              ✓ Live
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          {formatDate(c.sent_at)}
                          {c.published_on_website && c.slug && (
                            <> · <a href={`/insights/${c.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#658a07] hover:underline">/insights/{c.slug}</a></>
                          )}
                        </p>
                      </button>
                      <div className="flex items-center gap-3">
                        <div className="flex gap-3 text-right">
                          <div>
                            <p className="text-lg font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{c.sent_count}</p>
                            <p className="text-[10px] text-gray-400 uppercase">gesendet</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{c.opened_count}</p>
                            <p className="text-[10px] text-gray-400 uppercase">geöffnet</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{c.open_rate}%</p>
                            <p className="text-[10px] text-gray-400 uppercase">Open-Rate</p>
                          </div>
                        </div>
                        {c.published_on_website && (
                          <div className="flex flex-col gap-1 border-l border-gray-200 pl-3">
                            <button onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                              className="text-[10px] uppercase tracking-wider bg-[#0a0a0a] text-[#befa0a] px-2 py-1 hover:bg-[#befa0a] hover:text-[#0a0a0a]"
                              style={{ fontFamily: "'Oswald', sans-serif" }}
                              title="Live-Beitrag bearbeiten (Umlauts, Headline, Text)">
                              Bearbeiten
                            </button>
                            <button onClick={(e) => unpublishCampaign(c, e)}
                              className="text-[10px] uppercase tracking-wider border border-red-300 text-red-600 px-2 py-1 hover:bg-red-50"
                              style={{ fontFamily: "'Oswald', sans-serif" }}
                              title="Von Website nehmen (Insights-URL geht offline)">
                              Offline nehmen
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 py-4">
            <p className="text-xs text-gray-500 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Versendet: {formatDate(selected.sent_at)} · {selected.sent_count} Empfänger · {selected.opened_count} geöffnet ({selected.open_rate}%)
            </p>
            {loadingDetail ? <p className="text-gray-400 text-sm">Lädt...</p> : (
              <div className="space-y-1">
                {recipients.map((r, i) => (
                  <div key={i} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                    <p className="text-sm text-[#1f1f1f] truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>{r.email}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-gray-400">{formatDate(r.sent_at)}</span>
                      {r.opened_at ? (
                        <span className="text-emerald-600 font-bold flex items-center gap-1">
                          ✓ geöffnet {r.opened_count > 1 ? `(${r.opened_count}×)` : ''}
                        </span>
                      ) : (
                        <span className="text-gray-400">nicht geöffnet</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {editing && (
          <EditPublishedCampaignModal
            campaign={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); reloadCampaigns(); }}
          />
        )}
    </Panel>
  );
}

// ── Edit Published Newsletter Campaign (Live-Beitrag korrigieren) ────────────
function EditPublishedCampaignModal({ campaign, onClose, onSaved }) {
  const [form, setForm] = useState({
    headline: campaign.headline || '',
    intro: campaign.intro || '',
    usecase: campaign.usecase || '',
    technology: campaign.technology || '',
    cta_text: campaign.cta_text || '',
    summary: campaign.summary || '',
    image_url: '',  // Bild-URL ist nicht in list_campaigns — Sebastian kann hier nachschieben
    category: 'Insight',
    slug: campaign.slug || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true); setError('');
    try {
      // Nur Felder schicken die Sebastian wirklich geändert hat (string-Vergleich)
      // → image_url leer = nicht überschreiben, slug leer = nicht ändern
      const payload = {};
      if (form.headline !== campaign.headline) payload.headline = form.headline;
      if (form.intro !== campaign.intro) payload.intro = form.intro;
      if (form.usecase !== campaign.usecase) payload.usecase = form.usecase;
      if (form.technology !== campaign.technology) payload.technology = form.technology;
      if (form.cta_text !== campaign.cta_text) payload.cta_text = form.cta_text;
      if (form.summary !== campaign.summary) payload.summary = form.summary;
      if (form.image_url) payload.image_url = form.image_url;
      if (form.slug && form.slug !== campaign.slug) payload.slug = form.slug;
      payload.category = form.category;

      await axios.patch(`${API}/newsletter/campaigns/${campaign.id}/publish`, payload, { headers: authHeaders() });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Live-Beitrag bearbeiten
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-[11px] text-gray-500">
            Diese Änderungen gehen sofort live unter <code className="text-[#658a07]">/insights/{campaign.slug}</code>.
            Slug nur ändern wenn du sicher bist (alte URL bricht!).
          </p>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Headline</label>
            <input type="text" value={form.headline} onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Intro</label>
            <textarea value={form.intro} onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-y" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Use-Case (Hauptteil)</label>
            <textarea value={form.usecase} onChange={(e) => setForm((f) => ({ ...f, usecase: e.target.value }))}
              rows={8}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-y" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Technologie / Details</label>
            <textarea value={form.technology} onChange={(e) => setForm((f) => ({ ...f, technology: e.target.value }))}
              rows={5}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-y" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">CTA-Text (Newsletter-Footer)</label>
            <input type="text" value={form.cta_text} onChange={(e) => setForm((f) => ({ ...f, cta_text: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Teaser (Blog-Card auf Homepage)</label>
            <textarea value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              rows={2} maxLength={240}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none" />
            <p className="text-[10px] text-gray-400 mt-1">{(form.summary || '').length}/240</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Header-Bild</label>
              <ImageUploadField
                value={form.image_url}
                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">URL-Slug</label>
              <input type="text" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              <p className="text-[10px] text-amber-700 mt-1">⚠ Slug-Änderung bricht alte URL!</p>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Abbrechen
          </button>
          <button onClick={save} disabled={saving}
            className="bg-[#0a0a0a] text-[#befa0a] px-5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {saving ? 'Speichere…' : 'Live speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Image Upload Field (Datei-Picker + Vorschau für Insights-Bilder) ────────
function ImageUploadField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Datei > 5 MB. Bitte kleiner.'); return; }
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload/image`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload fehlgeschlagen.');
    }
    setUploading(false);
  };

  return (
    <div>
      {value && (
        <div className="mb-2 relative aspect-[16/9] bg-gray-50 border border-gray-200 overflow-hidden">
          <img src={value} alt="Header-Bild Vorschau" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-black/70 text-white text-[10px] px-2 py-0.5 hover:bg-black"
            title="Bild entfernen">
            ✕
          </button>
        </div>
      )}
      <label className="inline-flex items-center gap-2 cursor-pointer border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50"
        style={{ fontFamily: "'Oswald', sans-serif" }}>
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(e) => upload(e.target.files?.[0])}
          disabled={uploading}
          className="hidden" />
        {uploading ? '⌛ Lade hoch…' : (value ? '↻ Anderes Bild wählen' : '📷 Bild hochladen')}
      </label>
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder="oder URL eingeben"
        className="w-full border border-gray-300 px-2 py-1 text-[11px] mt-2 focus:outline-none focus:border-[#befa0a]" />
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
      <p className="text-[10px] text-gray-400 mt-1">JPG / PNG / WebP / GIF, max 5 MB.</p>
    </div>
  );
}


// ── Research Agent Modal (Lead-Gen) ──────────────────────────────────────────
function ResearchModal({ onClose, inline = false }) {
  // Form state
  const [industry, setIndustry] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [radiusKm, setRadiusKm] = useState(5);

  // Results
  const [companies, setCompanies] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Async states
  const [searching, setSearching] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [detailCompany, setDetailCompany] = useState(null);
  const [viewFilter, setViewFilter] = useState('open'); // 'open' | 'in_crm'

  const loadExisting = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/research/companies`, { headers: authHeaders() });
      setCompanies(data);
    } catch { setCompanies([]); }
    setLoading(false);
  };

  useEffect(() => { loadExisting(); }, []);

  const runSearch = async () => {
    if (!industry.trim() || !city.trim()) {
      setError('Branche und Stadt sind Pflicht');
      return;
    }
    setSearching(true); setError(''); setInfo('');
    setSelectedIds(new Set());
    try {
      const { data } = await axios.post(`${API}/research/search`, {
        industry: industry.trim(),
        city: city.trim(),
        postal_code: postalCode.trim(),
        radius_km: radiusKm,
      }, { headers: authHeaders() });
      setCompanies(data.companies || []);
      setInfo(`${data.found} Firmen gefunden in ${data.geocoded || city}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Suche fehlgeschlagen');
    }
    setSearching(false);
  };

  const filteredCompanies = companies.filter(c => viewFilter === 'in_crm' ? !!c.lead_id : !c.lead_id);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredCompanies.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCompanies.map(c => c.id)));
    }
  };

  const enrichSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Keine Firmen ausgewählt');
      return;
    }
    setEnriching(true); setError(''); setInfo('');
    try {
      const { data } = await axios.post(`${API}/research/enrich-batch`, { company_ids: ids }, { headers: authHeaders() });
      // Merge enriched data back into companies list
      const enrichedMap = new Map((data.enriched || []).map(e => [e.id, e]));
      setCompanies(prev => prev.map(c => enrichedMap.has(c.id) ? { ...c, ...enrichedMap.get(c.id) } : c));
      setInfo(`${(data.enriched || []).length} Firmen angereichert${data.failed?.length ? ` (${data.failed.length} Fehler)` : ''}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Anreicherung fehlgeschlagen');
    }
    setEnriching(false);
  };

  const transferToCrm = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Keine Firmen ausgewählt');
      return;
    }
    if (!window.confirm(`${ids.length} Firmen als Leads ins CRM übertragen?`)) return;
    setTransferring(true); setError(''); setInfo('');
    try {
      const { data } = await axios.post(`${API}/research/to-crm`, { company_ids: ids }, { headers: authHeaders() });
      const created = (data.created || []).length;
      const skipped = data.skipped || [];
      if (created > 0) {
        setInfo(`✓ ${created} als Lead angelegt${skipped.length ? ` · ${skipped.length} übersprungen` : ''}`);
      }
      if (skipped.length > 0 && created === 0) {
        // alle gescheitert — zeig den Grund der ersten
        const firstReason = skipped[0].reason || 'unbekannt';
        setError(`Übertragung fehlgeschlagen: ${firstReason}`);
      } else if (skipped.length > 0) {
        // einige übersprungen — zeig kurze info dazu
        const reasons = [...new Set(skipped.map(s => s.reason).filter(Boolean))].slice(0, 3).join(' · ');
        setInfo(prev => `${prev}${reasons ? ' — ' + reasons : ''}`);
      }
      setSelectedIds(new Set());
      // Mark transferred companies with lead_id so badge shows
      const createdLeadByCompany = new Map();
      (data.created || []).forEach((lead, i) => {
        if (ids[i]) createdLeadByCompany.set(ids[i], lead.id);
      });
      setCompanies(prev => prev.map(c => createdLeadByCompany.has(c.id) ? { ...c, lead_id: createdLeadByCompany.get(c.id) } : c));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Übertragung fehlgeschlagen';
      setError(`Übertragung fehlgeschlagen: ${detail}`);
    }
    setTransferring(false);
  };

  const allSelected = selectedIds.size > 0 && selectedIds.size === filteredCompanies.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filteredCompanies.length;

  return (
    <Panel inline={inline} onClose={onClose} maxWidth="max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-2 min-w-0">
          <Search size={20} className="text-[#658a07] flex-shrink-0" />
          <h2 className="text-base md:text-lg font-bold text-[#1f1f1f] uppercase tracking-tight truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Research · Lead-Gen
          </h2>
        </div>
        {!inline && <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>}
      </div>

      {/* Search Form — mobile: stacked, desktop: grid */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-gray-50">
        <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-12 md:gap-3">
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Branche *</label>
            <input value={industry} onChange={e => setIndustry(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch()}
              placeholder="z.B. Gastronomie, Friseur"
              className="w-full border border-gray-300 rounded px-3 py-2.5 text-base md:text-sm focus:outline-none focus:border-[#befa0a]"
              style={{ fontFamily: "'Manrope', sans-serif" }} />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Stadt *</label>
            <input value={city} onChange={e => setCity(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch()}
              placeholder="z.B. Kassel"
              className="w-full border border-gray-300 rounded px-3 py-2.5 text-base md:text-sm focus:outline-none focus:border-[#befa0a]"
              style={{ fontFamily: "'Manrope', sans-serif" }} />
          </div>
          {/* PLZ + Umkreis side-by-side on mobile, individual cols on desktop */}
          <div className="grid grid-cols-2 gap-3 md:contents">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>PLZ</label>
              <input value={postalCode} onChange={e => setPostalCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runSearch()}
                placeholder="34117"
                className="w-full border border-gray-300 rounded px-3 py-2.5 text-base md:text-sm focus:outline-none focus:border-[#befa0a]"
                style={{ fontFamily: "'Manrope', sans-serif" }} />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Umkreis: {radiusKm} km</label>
              <input type="range" min={1} max={25} value={radiusKm} onChange={e => setRadiusKm(parseInt(e.target.value))}
                className="w-full accent-[#befa0a] h-10 md:h-auto" />
            </div>
          </div>
          <div className="md:col-span-2 flex items-end">
            <button onClick={runSearch} disabled={searching || !industry.trim() || !city.trim()}
              className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 md:py-2 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-40 text-sm md:text-xs"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {searching ? 'Sucht...' : 'Suchen'}
            </button>
          </div>
        </div>
        {error && <p className="text-red-500 text-xs mt-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{error}</p>}
        {info && <p className="text-[#658a07] text-xs mt-2 font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>{info}</p>}
      </div>

      {/* Filter Tabs */}
      {companies.length > 0 && (
        <div className="bg-white border-b border-gray-100 px-3 md:px-6 pt-3">
          <div className="flex gap-2">
            {[
              { id: 'open', label: 'Offen', count: companies.filter(c => !c.lead_id).length },
              { id: 'in_crm', label: 'Im CRM', count: companies.filter(c => !!c.lead_id).length },
            ].map(tab => {
              const active = viewFilter === tab.id;
              return (
                <button key={tab.id} onClick={() => { setViewFilter(tab.id); setSelectedIds(new Set()); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
                    active ? 'border-[#befa0a] text-[#1f1f1f]' : 'border-transparent text-gray-400 hover:text-gray-700'
                  }`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {tab.label}
                  <span className={`text-[10px] rounded-full px-1.5 min-w-[20px] text-center ${active ? 'bg-[#befa0a] text-[#0a0a0a]' : 'bg-gray-100 text-gray-500'}`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Bar — nur im "Offen"-Tab, mobile: stacked, desktop: one row */}
      {companies.length > 0 && viewFilter === 'open' && (
        <div className="px-4 md:px-6 py-3 border-b border-gray-100 bg-white sticky top-0 z-[5] space-y-2 md:space-y-0 md:flex md:items-center md:justify-between md:gap-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm md:text-xs text-gray-700" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <input type="checkbox" checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleAll}
              className="w-5 h-5 md:w-4 md:h-4 accent-[#befa0a]" />
            <span className="font-medium">{selectedIds.size} / {filteredCompanies.length} ausgewählt</span>
          </label>
          <div className="flex items-stretch gap-2">
            <button onClick={enrichSelected} disabled={enriching || selectedIds.size === 0}
              className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-white border-2 border-gray-300 text-gray-700 px-3 py-2.5 md:py-1.5 text-sm md:text-xs font-bold uppercase rounded hover:border-[#befa0a] hover:text-[#658a07] disabled:opacity-40 active:bg-gray-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Sparkles size={14} /> {enriching ? `Reichere an...` : `Anreichern (${selectedIds.size})`}
            </button>
            <button onClick={transferToCrm} disabled={transferring || selectedIds.size === 0}
              className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-[#0a0a0a] text-white px-3 py-2.5 md:py-1.5 text-sm md:text-xs font-bold uppercase rounded hover:bg-gray-800 disabled:opacity-40 active:bg-gray-900"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Plus size={14} /> {transferring ? 'Überträgt...' : `Ins CRM (${selectedIds.size})`}
            </button>
          </div>
        </div>
      )}

      {/* Loading state during search */}
      {searching && (
        <div className="px-6 py-12 text-center">
          <div className="inline-block w-6 h-6 border-2 border-[#befa0a] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>Geocoding + Google Places werden abgefragt...</p>
        </div>
      )}

      {/* Loading state during enrichment */}
      {enriching && (
        <div className="px-6 py-4 bg-[#befa0a]/5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-[#658a07] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-[#658a07] font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Reichere {selectedIds.size} Firmen an — Impressum scrapen + Claude analysiert (kann 30-90 Sek dauern)
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!searching && companies.length === 0 && !loading && (
        <div className="px-4 md:px-6 py-10 md:py-16 text-center">
          <Search size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Branche, Stadt und PLZ eingeben → "Suchen" — bis zu 25 Firmen werden gefunden.
          </p>
          <p className="text-xs text-gray-400 mt-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Danach: Auswählen → Anreichern (E-Mail, Geschäftsführer, KI-Use-Case) → Ins CRM.
          </p>
        </div>
      )}

      {companies.length > 0 && filteredCompanies.length === 0 && (
        <div className="px-4 md:px-6 py-12 text-center">
          <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {viewFilter === 'in_crm' ? 'Noch keine Firmen ins CRM übertragen.' : 'Alle Firmen wurden ins CRM übertragen.'}
          </p>
        </div>
      )}

      {filteredCompanies.length > 0 && (
        <>
          {/* Mobile: Cards */}
          <div className="md:hidden divide-y divide-gray-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {filteredCompanies.map(c => {
              const checked = selectedIds.has(c.id);
              const inCrm = !!c.lead_id;
              const enrichedHasData = c.enriched_at && (c.email || c.managing_director || c.main_services);
              return (
                <div key={c.id}
                  className={`flex gap-3 px-4 py-4 active:bg-gray-50 ${checked ? 'bg-[#befa0a]/5' : ''} ${inCrm ? 'opacity-60' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleSelect(c.id)}
                    onClick={e => e.stopPropagation()}
                    className="w-6 h-6 accent-[#befa0a] flex-shrink-0 mt-0.5" disabled={inCrm} />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setDetailCompany(c)}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-bold text-base text-[#1f1f1f] leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>{c.name}</p>
                      {inCrm ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold uppercase flex-shrink-0">✓ CRM</span>
                      ) : enrichedHasData ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#befa0a]/20 text-[#658a07] rounded font-bold uppercase flex-shrink-0">+Daten</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-bold uppercase flex-shrink-0">Basis</span>
                      )}
                    </div>
                    {c.industry && <p className="text-xs text-gray-500 mb-1">{c.industry}</p>}
                    {c.address && (
                      <p className="text-xs text-gray-600 mb-1.5 leading-snug">📍 {c.address}</p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()}
                          className="text-[#658a07] font-medium">📞 {c.phone}</a>
                      )}
                      {c.email && (
                        <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()}
                          className="text-[#658a07] font-medium truncate">✉ {c.email}</a>
                      )}
                      {c.google_rating && (
                        <span className="text-gray-600 flex items-center gap-0.5">
                          <Star size={11} className="text-yellow-500" />
                          {c.google_rating.toFixed(1)} ({c.google_review_count || 0})
                        </span>
                      )}
                      {c.website && (
                        <a href={c.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          className="text-[#658a07] font-medium flex items-center gap-1 truncate">
                          <Globe size={11} /> {c.domain || 'Website'}
                        </a>
                      )}
                    </div>
                    {c.managing_director && (
                      <p className="text-xs text-gray-700 mt-1.5"><span className="text-gray-400">GF:</span> {c.managing_director}</p>
                    )}
                    {c.main_services && (
                      <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{c.main_services}</p>
                    )}
                    {c.ai_opportunities && (
                      <div className="mt-2 p-2 bg-[#befa0a]/10 border border-[#befa0a]/30 rounded">
                        <p className="text-[10px] uppercase tracking-wider text-[#658a07] font-bold mb-0.5">💡 KI-Use-Case</p>
                        <p className="text-xs text-gray-800 leading-relaxed">{c.ai_opportunities}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500" style={{ fontFamily: "'Oswald', sans-serif" }}>
                <tr>
                  <th className="px-3 py-2 text-left w-10"></th>
                  <th className="px-3 py-2 text-left">Firma</th>
                  <th className="px-3 py-2 text-left">Adresse</th>
                  <th className="px-3 py-2 text-left hidden lg:table-cell">Kontakt</th>
                  <th className="px-3 py-2 text-left">Rating</th>
                  <th className="px-3 py-2 text-left hidden lg:table-cell">Status</th>
                </tr>
              </thead>
              <tbody style={{ fontFamily: "'Manrope', sans-serif" }}>
                {filteredCompanies.map(c => {
                  const checked = selectedIds.has(c.id);
                  const inCrm = !!c.lead_id;
                  const enrichedHasData = c.enriched_at && (c.email || c.managing_director || c.main_services);
                  return (
                    <tr key={c.id} className={`border-b border-gray-100 hover:bg-gray-50 ${checked ? 'bg-[#befa0a]/5' : ''} ${inCrm ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={checked} onChange={() => toggleSelect(c.id)}
                          className="w-4 h-4 accent-[#befa0a] cursor-pointer" disabled={inCrm} />
                      </td>
                      <td className="px-3 py-3 cursor-pointer" onClick={() => setDetailCompany(c)}>
                        <div className="min-w-0">
                          <p className="font-bold text-[#1f1f1f] truncate">{c.name}</p>
                          {c.industry && <p className="text-[11px] text-gray-500">{c.industry}</p>}
                          {c.website && (
                            <a href={c.website} target="_blank" rel="noopener noreferrer"
                              className="text-[11px] text-[#658a07] hover:underline flex items-center gap-1 truncate">
                              <Globe size={10} /> {c.domain || c.website}
                            </a>
                          )}
                          {c.main_services && (
                            <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{c.main_services}</p>
                          )}
                          {c.ai_opportunities && (
                            <p className="text-[11px] text-[#658a07] mt-1 line-clamp-2">💡 {c.ai_opportunities.slice(0, 120)}{c.ai_opportunities.length > 120 ? '...' : ''}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600 max-w-xs">
                        <div className="truncate">{c.address || '–'}</div>
                        {c.postal_code && <div className="text-[10px] text-gray-400">{c.postal_code} {c.city}</div>}
                      </td>
                      <td className="px-3 py-3 hidden lg:table-cell text-xs">
                        {c.phone && <div className="text-gray-700">{c.phone}</div>}
                        {c.email && <div className="text-[#658a07] truncate">{c.email}</div>}
                        {c.managing_director && <div className="text-gray-500 text-[11px]">GF: {c.managing_director}</div>}
                        {c.employees_estimate && <div className="text-gray-400 text-[10px]">{c.employees_estimate} MA</div>}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {c.google_rating ? (
                          <div className="flex items-center gap-1">
                            <Star size={10} className="text-yellow-500" />
                            {c.google_rating.toFixed(1)}
                            <span className="text-gray-400">({c.google_review_count || 0})</span>
                          </div>
                        ) : '–'}
                      </td>
                      <td className="px-3 py-3 hidden lg:table-cell text-[10px]">
                        {inCrm ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold uppercase">✓ Im CRM</span>
                        ) : enrichedHasData ? (
                          <span className="px-2 py-0.5 bg-[#befa0a]/20 text-[#658a07] rounded font-bold uppercase">Angereichert</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-bold uppercase">Basis</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detail Drawer for one company */}
      {detailCompany && (
        <div className="fixed inset-0 z-50 flex" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <div className="flex-1 bg-black/60" onClick={() => setDetailCompany(null)} />
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-base font-bold text-[#1f1f1f] uppercase tracking-tight truncate pr-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {detailCompany.name}
              </h3>
              <button onClick={() => setDetailCompany(null)} className="text-gray-400 hover:text-gray-600 p-1.5 flex-shrink-0"><X size={20} /></button>
            </div>
            <div className="px-5 py-4 space-y-4 text-sm">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 text-xs">
                {detailCompany.industry && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">{detailCompany.industry}</span>}
                {detailCompany.employees_estimate && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">{detailCompany.employees_estimate} MA</span>}
                {detailCompany.lead_id ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">✓ Im CRM</span>
                ) : detailCompany.enriched_at ? (
                  <span className="px-2 py-0.5 bg-[#befa0a]/20 text-[#658a07] rounded font-bold">Angereichert</span>
                ) : (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-bold">Basis</span>
                )}
              </div>

              {/* Kontakt-Block */}
              <div className="space-y-2">
                {detailCompany.address && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-base">📍</span>
                    <div>
                      <p className="text-gray-700">{detailCompany.address}</p>
                      {detailCompany.postal_code && <p className="text-xs text-gray-400">{detailCompany.postal_code} {detailCompany.city}</p>}
                    </div>
                  </div>
                )}
                {detailCompany.phone && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-base">📞</span>
                    <a href={`tel:${detailCompany.phone}`} className="text-[#658a07] hover:underline">{detailCompany.phone}</a>
                  </div>
                )}
                {detailCompany.email && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-base">✉</span>
                    <a href={`mailto:${detailCompany.email}`} className="text-[#658a07] hover:underline break-all">{detailCompany.email}</a>
                  </div>
                )}
                {detailCompany.website && (
                  <div className="flex items-center gap-2">
                    <Globe size={14} className="text-gray-400" />
                    <a href={detailCompany.website} target="_blank" rel="noopener noreferrer" className="text-[#658a07] hover:underline truncate">
                      {detailCompany.website}
                    </a>
                  </div>
                )}
                {detailCompany.linkedin_url && (
                  <div className="flex items-center gap-2">
                    <Linkedin size={14} className="text-gray-400" />
                    <a href={detailCompany.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#658a07] hover:underline truncate">
                      LinkedIn
                    </a>
                  </div>
                )}
                {detailCompany.google_rating && (
                  <div className="flex items-center gap-2">
                    <Star size={14} className="text-yellow-500" />
                    <span className="text-gray-700">{detailCompany.google_rating.toFixed(1)} ({detailCompany.google_review_count || 0} Bewertungen)</span>
                  </div>
                )}
              </div>

              {/* Geschäftsführer */}
              {(detailCompany.managing_director || detailCompany.contact_person) && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Ansprechpartner</p>
                  {detailCompany.managing_director && <p className="text-sm"><span className="text-gray-500">Geschäftsführer:</span> <span className="text-[#1f1f1f] font-medium">{detailCompany.managing_director}</span></p>}
                  {detailCompany.contact_person && <p className="text-sm"><span className="text-gray-500">Kontakt:</span> <span className="text-[#1f1f1f] font-medium">{detailCompany.contact_person}</span></p>}
                </div>
              )}

              {/* Leistungen */}
              {detailCompany.main_services && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>Leistungen / Was sie machen</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{detailCompany.main_services}</p>
                </div>
              )}

              {/* AI Opportunities */}
              {detailCompany.ai_opportunities && (
                <div className="border-2 border-[#befa0a]/40 bg-[#befa0a]/5 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#658a07] font-bold mb-1.5 flex items-center gap-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <Sparkles size={12} /> KI-Anwendungsfälle (Claude)
                  </p>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{detailCompany.ai_opportunities}</p>
                </div>
              )}

              {/* Opening Hours */}
              {detailCompany.opening_hours && Array.isArray(detailCompany.opening_hours) && detailCompany.opening_hours.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>Öffnungszeiten</p>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    {detailCompany.opening_hours.map((h, i) => <div key={i}>{h}</div>)}
                  </div>
                </div>
              )}

              {/* Deep-Research (News/Perplexity) */}
              {detailCompany.deep_research && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    📰 Deep-Research (News + Trends)
                  </p>
                  <p className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap">{detailCompany.deep_research}</p>
                  {detailCompany.deep_research_citations && detailCompany.deep_research_citations.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-blue-700 hover:underline">{detailCompany.deep_research_citations.length} Quellen</summary>
                      <div className="mt-1.5 space-y-1">
                        {detailCompany.deep_research_citations.slice(0, 10).map((c, i) => {
                          const url = typeof c === 'string' ? c : (c.url || '');
                          return url ? (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-700 hover:underline truncate">
                              {i + 1}. {url}
                            </a>
                          ) : null;
                        })}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Imprint URL */}
              {detailCompany.imprint_url && (
                <div className="border-t border-gray-100 pt-3 text-xs">
                  <a href={detailCompany.imprint_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#658a07] hover:underline">
                    → Impressum öffnen
                  </a>
                </div>
              )}

              {/* Status meta */}
              <div className="border-t border-gray-100 pt-3 text-[11px] text-gray-400 space-y-0.5">
                {detailCompany.researched_at && <p>Recherchiert: {formatDate(detailCompany.researched_at)}</p>}
                {detailCompany.enriched_at && <p>Angereichert: {formatDate(detailCompany.enriched_at)}</p>}
              </div>

              {/* Action: not yet enriched */}
              {!detailCompany.enriched_at && (
                <button onClick={async () => {
                  setEnriching(true);
                  try {
                    const { data } = await axios.post(`${API}/research/enrich-batch`, { company_ids: [detailCompany.id] }, { headers: authHeaders() });
                    const enriched = (data.enriched || [])[0];
                    if (enriched) {
                      setCompanies(prev => prev.map(x => x.id === enriched.id ? { ...x, ...enriched } : x));
                      setDetailCompany({ ...detailCompany, ...enriched });
                    }
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Anreicherung fehlgeschlagen');
                  }
                  setEnriching(false);
                }} disabled={enriching}
                  className="w-full bg-white border-2 border-gray-300 text-gray-700 py-2.5 text-sm font-bold uppercase rounded hover:border-[#befa0a] hover:text-[#658a07] disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <Sparkles size={14} /> {enriching ? 'Reichere an...' : 'Diese Firma anreichern'}
                </button>
              )}

              {/* Action: Deep-Research (Perplexity News) */}
              {!detailCompany.deep_research_at && (
                <button onClick={async () => {
                  setEnriching(true); setError('');
                  try {
                    const { data } = await axios.post(`${API}/research/company/${detailCompany.id}/deep-research`, {}, { headers: authHeaders() });
                    setDetailCompany(data);
                    setCompanies(prev => prev.map(x => x.id === data.id ? { ...x, ...data } : x));
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Deep-Research fehlgeschlagen');
                  }
                  setEnriching(false);
                }} disabled={enriching}
                  className="w-full bg-blue-50 border-2 border-blue-200 text-blue-700 py-2.5 text-sm font-bold uppercase rounded hover:bg-blue-100 disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  📰 {enriching ? 'Recherchiere News...' : 'Deep-Research (News + Trends)'}
                </button>
              )}

              {/* Action: to CRM */}
              {!detailCompany.lead_id && (
                <button onClick={async () => {
                  if (!window.confirm(`"${detailCompany.name}" als Lead ins CRM übertragen?`)) return;
                  setTransferring(true);
                  try {
                    const { data } = await axios.post(`${API}/research/to-crm`, { company_ids: [detailCompany.id] }, { headers: authHeaders() });
                    const newLead = (data.created || [])[0];
                    if (newLead) {
                      setCompanies(prev => prev.map(x => x.id === detailCompany.id ? { ...x, lead_id: newLead.id } : x));
                      setDetailCompany({ ...detailCompany, lead_id: newLead.id });
                      setInfo(`✓ Als Lead angelegt: ${detailCompany.name}`);
                    } else {
                      const skip = (data.skipped || [])[0];
                      setError(`Nicht übertragen: ${skip?.reason || 'unbekannt'}`);
                    }
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Übertragung fehlgeschlagen');
                  }
                  setTransferring(false);
                }} disabled={transferring}
                  className="w-full bg-[#0a0a0a] text-white py-2.5 text-sm font-bold uppercase rounded hover:bg-gray-800 disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <Plus size={14} /> {transferring ? 'Überträgt...' : 'Ins CRM übertragen'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

// ── Subscriber List Modal ────────────────────────────────────────────────────
function SubscribersModal({ onClose, onOpenLead, inline = false }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | confirmed | pending | unsubscribed | with_lead

  useEffect(() => {
    axios.get(`${API}/newsletter/subscribers`, { headers: authHeaders() })
      .then(({ data }) => setSubs(data))
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = subs.filter(s => {
    const q = search.toLowerCase();
    if (q && !((s.email || '').toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q))) return false;
    if (filter === 'confirmed' && !s.confirmed) return false;
    if (filter === 'pending' && (s.confirmed || s.unsubscribed)) return false;
    if (filter === 'unsubscribed' && !s.unsubscribed) return false;
    if (filter === 'with_lead' && !s.lead) return false;
    return true;
  });

  const stats = {
    total: subs.length,
    confirmed: subs.filter(s => s.confirmed && !s.unsubscribed).length,
    pending: subs.filter(s => !s.confirmed && !s.unsubscribed).length,
    unsubscribed: subs.filter(s => s.unsubscribed).length,
    with_lead: subs.filter(s => s.lead).length,
  };

  return (
    <Panel inline={inline} onClose={onClose} maxWidth="max-w-3xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-[#1f1f1f] uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Newsletter Abonnenten
          </h2>
          {!inline && <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-6 py-4 bg-gray-50 border-b border-gray-100">
          {[
            ['Gesamt', stats.total, 'all'],
            ['Bestätigt', stats.confirmed, 'confirmed'],
            ['Ausstehend', stats.pending, 'pending'],
            ['Abgemeldet', stats.unsubscribed, 'unsubscribed'],
            ['Mit Lead', stats.with_lead, 'with_lead'],
          ].map(([label, count, f]) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-left p-3 rounded-lg border-2 transition-colors ${filter === f ? 'border-[#befa0a] bg-white' : 'border-transparent bg-white hover:border-gray-200'}`}>
              <p className="text-2xl font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{count}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500" style={{ fontFamily: "'Oswald', sans-serif" }}>{label}</p>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="E-Mail oder Name suchen..."
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]"
            style={{ fontFamily: "'Manrope', sans-serif" }} />
        </div>

        {/* List */}
        <div className="flex-1 px-6 py-4">
          {loading ? <p className="text-gray-400 text-sm">Lädt...</p> : filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-12 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>Keine Einträge.</p>
          ) : (
            <div className="space-y-2">
              {filtered.map((s, i) => (
                <div key={i} className="border border-gray-200 rounded p-3 hover:border-gray-300 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#1f1f1f] truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {s.name || 'Ohne Namen'}
                      </p>
                      <p className="text-xs text-gray-500 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.email}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                          s.unsubscribed ? 'bg-gray-100 text-gray-500' :
                          s.confirmed ? 'bg-emerald-100 text-emerald-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {s.unsubscribed ? 'Abgemeldet' : s.confirmed ? '✓ Bestätigt' : 'Ausstehend'}
                        </span>
                        {s.lead && (
                          <button onClick={() => onOpenLead?.(s.lead.id)}
                            className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-[#befa0a]/20 text-[#658a07] hover:bg-[#befa0a]/40 transition-colors">
                            🔗 Lead: {s.lead.company_name || s.lead.contact_name || '—'}
                          </button>
                        )}
                        <span className="text-[10px] text-gray-400">Quelle: {s.source || '—'}</span>
                      </div>
                    </div>
                    <div className="text-right text-[10px] text-gray-400" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <p>angemeldet</p>
                      <p>{formatDate(s.created_at)}</p>
                      {s.confirmed_at && (
                        <>
                          <p className="mt-1">bestätigt</p>
                          <p>{formatDate(s.confirmed_at)}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
    </Panel>
  );
}

// ── Newsletter Composer Modal ────────────────────────────────────────────────
function NewsletterComposerModal({ onClose, inline = false }) {
  const [step, setStep] = useState('discover'); // discover | choose | research | edit | sent
  const [topic, setTopic] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [discoverMeta, setDiscoverMeta] = useState(null);
  const [research, setResearch] = useState('');
  const [citations, setCitations] = useState([]);
  const [draft, setDraft] = useState({ headline: '', intro: '', usecase: '', technology: '', cta_text: '' });
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [linkedinPosts, setLinkedinPosts] = useState(null);   // {posts: [{variant,text,hashtags}]}
  const [linkedinBusy, setLinkedinBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [overlayConfig, setOverlayConfig] = useState(null);   // {title,subtitle,steps,estimatedSeconds} | null
  const { run: runAsync, cancel: cancelAsync } = useAsyncTask();
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/newsletter/subscribers`, { headers: authHeaders() })
      .then(({ data }) => setStats({
        total: data.length,
        confirmed: data.filter(s => s.confirmed && !s.unsubscribed).length,
        pending: data.filter(s => !s.confirmed && !s.unsubscribed).length,
      }))
      .catch(() => {});
  }, []);

  const discoverTopics = async () => {
    setBusy(true); setError('');
    setOverlayConfig({
      title: 'Perplexity recherchiert',
      subtitle: 'Aktuelle KI-Trends im stationären Mittelstand werden zusammengetragen.',
      steps: ['Web-Search starten', 'Tier-1-Quellen scannen', '4 Themen vorschlagen + Begründung'],
      estimatedSeconds: 25,
      onCancel: cancelAsync,
    });
    try {
      const result = await runAsync('newsletter_discover', {});
      setSuggestions(result.topics || []);
      setDiscoverMeta(result._meta || null);
      setStep('choose');
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.message || 'Themen-Suche fehlgeschlagen');
      }
    }
    setBusy(false);
    setOverlayConfig(null);
  };

  const generateLinkedInPosts = async () => {
    setLinkedinBusy(true); setError('');
    setOverlayConfig({
      title: 'Claude generiert 2 LinkedIn-Varianten',
      subtitle: 'Story-Hook + Daten-Hook in deiner Stimme.',
      steps: ['Variante A: Story-Hook bauen', 'Variante B: Daten-Hook bauen', 'Hashtags wählen'],
      estimatedSeconds: 12,
      onCancel: cancelAsync,
    });
    try {
      const result = await runAsync('newsletter_linkedin', { topic, research, newsletter_html: null });
      setLinkedinPosts(result);
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.message || 'LinkedIn-Posts-Generierung fehlgeschlagen');
      }
    }
    setLinkedinBusy(false);
    setOverlayConfig(null);
  };

  const openPreview = async () => {
    setPreviewBusy(true); setError('');
    try {
      const sources = (citations || []).map(c => typeof c === 'string' ? c : (c.url || c)).filter(Boolean);
      const { data } = await axios.post(`${API}/newsletter/preview`,
        { ...draft, sources },
        { headers: authHeaders() });
      setPreviewHtml(data.html || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Preview-Generierung fehlgeschlagen');
    }
    setPreviewBusy(false);
  };

  const copyPostText = (idx, text, hashtags) => {
    const full = (text || '') + (hashtags?.length ? '\n\n' + hashtags.join(' ') : '');
    navigator.clipboard.writeText(full).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  const chooseTopic = async (topicTitle) => {
    setTopic(topicTitle);
    setBusy(true); setError('');
    setOverlayConfig({
      title: 'Perplexity recherchiert das Thema',
      subtitle: `"${topicTitle.slice(0, 80)}${topicTitle.length > 80 ? '…' : ''}"`,
      steps: [
        'Marktlage + Branchen-Trends',
        'Praxis-Beispiele aus Unternehmen',
        'Technologie + Kosten-Aufwand',
        'Stolperfallen + Quellen sammeln',
      ],
      estimatedSeconds: 30,
      onCancel: cancelAsync,
    });
    try {
      const result = await runAsync('newsletter_research', { topic: topicTitle });
      setResearch(result.research || '');
      setCitations(result.citations || []);
      setStep('research');
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.message || 'Recherche fehlgeschlagen');
      }
    }
    setBusy(false);
    setOverlayConfig(null);
  };

  const generate = async () => {
    setBusy(true); setError('');
    setOverlayConfig({
      title: 'Claude schreibt deinen Newsletter',
      subtitle: 'Headline, Intro, Use-Case-Story, Technologie-Block, CTA — alles in einem Wurf.',
      steps: ['Recherche durchgehen', 'Struktur bauen', 'Text schreiben', 'JSON validieren'],
      estimatedSeconds: 15,
      onCancel: cancelAsync,
    });
    try {
      const result = await runAsync('newsletter_draft', { topic, research });
      setDraft(result);
      setSubject(result.headline);
      setStep('edit');
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.message || 'Generierung fehlgeschlagen');
      }
    }
    setBusy(false);
    setOverlayConfig(null);
  };

  const send = async (asTest = false) => {
    setSending(true); setError('');
    try {
      const sources = (citations || []).map(c => typeof c === 'string' ? c : (c.url || c)).filter(Boolean);
      const { data } = await axios.post(`${API}/newsletter/send`, {
        ...draft, subject, sources, test_email: asTest ? testEmail : null,
      }, { headers: authHeaders() });
      setResult(data);
      if (!asTest) setStep('sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Versand fehlgeschlagen');
    }
    setSending(false);
  };

  const containerCls = inline
    ? "flex flex-col bg-white flex-1 min-h-0 overflow-y-auto"
    : "fixed inset-0 z-50 flex items-center justify-center";
  const innerCls = inline
    ? "p-6 md:p-10 max-w-3xl w-full mx-auto"
    : "relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto";
  return (
    <div className={containerCls}>
      {overlayConfig && <LongOperationOverlay {...overlayConfig} />}
      {previewHtml !== null && (
        <div className="fixed inset-0 z-[150] bg-black/70 flex items-center justify-center p-2 md:p-6" onClick={(e) => { if (e.target === e.currentTarget) setPreviewHtml(null); }}>
          <div className="bg-white w-full max-w-[720px] h-[calc(100vh-2rem)] flex flex-col">
            <div className="bg-[#0a0a0a] text-white px-4 py-3 flex items-center justify-between">
              <p className="text-sm font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>Newsletter-Vorschau</p>
              <button onClick={() => setPreviewHtml(null)} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <iframe title="Newsletter-Vorschau" srcDoc={previewHtml} className="flex-1 w-full border-0 bg-[#f5f5f5]" />
          </div>
        </div>
      )}
      {!inline && <div className="absolute inset-0 bg-black/50" onClick={onClose} />}
      <div className={innerCls}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold uppercase tracking-tight text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Newsletter erstellen
            </h3>
            {stats && (
              <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {stats.confirmed} bestätigte Empfänger · {stats.pending} ausstehend
              </p>
            )}
          </div>
          {!inline && <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>}
        </div>

        {step === 'discover' && (
          <div>
            <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Perplexity recherchiert aktuelle KI-Trends im Retail und schlägt 2 Newsletter-Themen vor. Du wählst eines aus.
            </p>
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Oder eigenes Thema eingeben</p>
              <textarea value={topic} onChange={e => setTopic(e.target.value)} rows={3}
                placeholder="z.B. Voice Agent für Friseursalon mit 3 Filialen"
                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              <button onClick={() => chooseTopic(topic)} disabled={busy || !topic.trim()}
                className="mt-2 w-full border border-gray-300 py-2 text-xs font-bold uppercase rounded hover:bg-gray-50 disabled:opacity-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                Eigenes Thema recherchieren
              </button>
            </div>
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <button onClick={discoverTopics} disabled={busy}
              className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {busy ? 'Perplexity sucht...' : 'Themen-Vorschläge laden'}
            </button>
          </div>
        )}

        {step === 'choose' && (
          <div>
            <p className="text-sm text-gray-600 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Wähle ein Thema für die tiefere Recherche:
            </p>
            {discoverMeta && (
              <p className="text-[11px] text-gray-400 italic mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Diese Woche fokus: <strong>{discoverMeta.week_focus_branche}</strong> · {discoverMeta.week_focus_aspekt}
                {discoverMeta.recent_titles_avoided > 0 && ` · ${discoverMeta.recent_titles_avoided} bisherige Titel werden vermieden`}
              </p>
            )}
            <div className="space-y-3">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => chooseTopic(s.title)} disabled={busy}
                  className="w-full text-left border-2 border-gray-200 hover:border-[#befa0a] hover:bg-[#befa0a]/5 rounded-lg p-4 transition-colors disabled:opacity-50">
                  <p className="text-[#1f1f1f] font-bold text-base mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{s.title}</p>
                  <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.rationale}</p>
                  {s.sources_hint && (
                    <p className="text-[10px] text-gray-400 mt-2 italic">Quellen: {s.sources_hint}</p>
                  )}
                </button>
              ))}
            </div>
            {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
            <button onClick={() => setStep('discover')}
              className="mt-4 w-full border border-gray-300 py-2 text-xs font-bold uppercase rounded hover:bg-gray-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Neue Themen laden
            </button>
          </div>
        )}

        {step === 'research' && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Thema</p>
            <p className="text-[#1f1f1f] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>{topic}</p>

            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Perplexity-Recherche</p>
            <textarea value={research} onChange={e => setResearch(e.target.value)} rows={10}
              className="w-full border border-gray-200 rounded px-3 py-2 text-xs leading-relaxed focus:outline-none focus:border-[#befa0a] mb-3"
              style={{ fontFamily: "'Manrope', sans-serif" }} />

            {citations.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">Quellen ({citations.length})</p>
                <ul className="text-xs space-y-1 list-disc list-inside text-gray-600">
                  {citations.slice(0, 5).map((c, i) => (
                    <li key={i} className="truncate"><a href={typeof c === 'string' ? c : c.url} target="_blank" rel="noopener noreferrer" className="text-[#658a07] hover:underline">{typeof c === 'string' ? c : c.url}</a></li>
                  ))}
                </ul>
              </div>
            )}

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setStep('discover')}
                className="border border-gray-300 px-4 py-3 text-sm font-bold uppercase rounded hover:bg-gray-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                Zurück
              </button>
              <button onClick={generate} disabled={busy}
                className="flex-1 bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {busy ? 'Claude schreibt...' : '✨ Newsletter generieren'}
              </button>
            </div>
          </div>
        )}

        {step === 'edit' && (
          <div className="space-y-3">
            {[
              ['Betreff', subject, setSubject, false],
              ['Headline', draft.headline, v => setDraft(d => ({ ...d, headline: v })), false],
              ['Einstieg', draft.intro, v => setDraft(d => ({ ...d, intro: v })), true],
              ['Use-Case', draft.usecase, v => setDraft(d => ({ ...d, usecase: v })), true],
              ['Technologie dahinter', draft.technology, v => setDraft(d => ({ ...d, technology: v })), true],
              ['CTA-Text', draft.cta_text, v => setDraft(d => ({ ...d, cta_text: v })), true],
            ].map(([label, val, setter, isTextarea]) => (
              <div key={label}>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">{label}</p>
                {isTextarea ? (
                  <textarea value={val} onChange={e => setter(e.target.value)} rows={3}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
                ) : (
                  <input value={val} onChange={e => setter(e.target.value)}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
                )}
              </div>
            ))}

            <div className="border border-gray-200 rounded p-3 mt-4 flex items-center gap-2">
              <button onClick={openPreview} disabled={previewBusy}
                className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider rounded hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-1.5"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {previewBusy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                Vorschau anzeigen
              </button>
              <span className="text-[11px] text-gray-500">— sieht aus wie die echte Mail</span>
            </div>

            <div className="border border-gray-200 rounded p-3 mt-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">Test-Versand an einzelne Adresse</p>
              <div className="flex gap-2">
                <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="dein@test.de"
                  className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
                <button onClick={() => send(true)} disabled={sending || !testEmail.trim()}
                  className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase rounded hover:bg-gray-50 disabled:opacity-50"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Test
                </button>
              </div>
              {result?.test && <p className="text-emerald-600 text-xs mt-2">Test-Mail gesendet ✓</p>}
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            {citations.length > 0 && (
              <div className="border border-gray-200 rounded p-3 bg-gray-50 mt-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">Quellen die mitgesendet werden ({citations.length})</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  {citations.slice(0, 5).map((c, i) => (
                    <li key={i} className="truncate"><a href={typeof c === 'string' ? c : c.url} target="_blank" rel="noopener noreferrer" className="text-[#658a07] hover:underline">{typeof c === 'string' ? c : c.url}</a></li>
                  ))}
                </ul>
              </div>
            )}

            {/* LinkedIn-Posts Generieren */}
            <div className="border border-blue-200 bg-blue-50/40 rounded p-3 mt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  LinkedIn-Posts zum Thema
                </p>
                {!linkedinPosts && (
                  <button onClick={generateLinkedInPosts} disabled={linkedinBusy}
                    className="text-[10px] uppercase tracking-wider bg-[#0077b5] text-white px-3 py-1.5 rounded hover:bg-[#005e8b] disabled:opacity-50 inline-flex items-center gap-1"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {linkedinBusy ? 'Generiere…' : '2 Varianten generieren'}
                  </button>
                )}
                {linkedinPosts && (
                  <button onClick={generateLinkedInPosts} disabled={linkedinBusy}
                    className="text-[10px] uppercase tracking-wider border border-gray-300 px-2 py-1 rounded hover:bg-gray-50"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {linkedinBusy ? '…' : 'Neu generieren'}
                  </button>
                )}
              </div>
              {linkedinPosts?.posts?.length ? (
                <div className="space-y-2 mt-2">
                  {linkedinPosts.posts.map((p, i) => (
                    <div key={i} className="border border-gray-200 bg-white rounded p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">{p.variant}</span>
                        <button onClick={() => copyPostText(i, p.text, p.hashtags)}
                          className="text-[10px] uppercase tracking-wider border border-gray-300 px-2 py-1 rounded hover:bg-gray-50"
                          style={{ fontFamily: "'Oswald', sans-serif" }}>
                          {copiedIdx === i ? '✓ Kopiert' : 'Text kopieren'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{p.text}</p>
                      {p.hashtags?.length > 0 && (
                        <p className="text-[10px] text-blue-600 mt-2">{p.hashtags.join(' ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-500 italic">Klick "Varianten generieren" — Story-Hook + Daten-Hook, je ~150-250 Wörter, copy-and-paste-fertig.</p>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={() => setStep('discover')} className="flex-1 border border-gray-300 py-3 font-bold uppercase rounded text-sm hover:bg-gray-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                Neues Thema
              </button>
              <button onClick={() => { if (window.confirm(`Newsletter an ${stats?.confirmed ?? '?'} Empfänger senden?`)) send(false); }} disabled={sending}
                className="flex-1 bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {sending ? 'Sende...' : `An alle (${stats?.confirmed ?? '?'}) senden`}
              </button>
            </div>
          </div>
        )}

        {step === 'sent' && result && (
          <PublishNewsletterPanel
            campaignId={result.campaign_id}
            initialHeadline={draft.headline}
            initialIntro={draft.intro}
            initialUsecase={draft.usecase}
            initialTechnology={draft.technology}
            initialCtaText={draft.cta_text}
            sentCount={result.sent}
            failedCount={result.failed}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

// ── Newsletter Publish Panel (im "sent"-Step) ────────────────────────────────
function PublishNewsletterPanel({ campaignId, initialHeadline, initialIntro, initialUsecase, initialTechnology, initialCtaText, sentCount, failedCount, onClose }) {
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    slug: '',
    category: 'Insight',
    summary: (initialIntro || '').slice(0, 200),
    image_url: '',
  });

  // Wenn Test-Send (kein campaign_id) — nur Erfolgsmeldung, kein Publish
  if (!campaignId) {
    return (
      <div className="text-center py-8">
        <div className="w-1.5 h-12 bg-[#befa0a] mx-auto mb-5" />
        <h3 className="text-[#1f1f1f] text-xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Test-Mail versendet
        </h3>
        <p className="text-gray-600 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Prüfe deinen Posteingang. Wenn alles passt, sende den Newsletter regulär.
        </p>
        <button onClick={onClose} className="bg-[#befa0a] text-[#0a0a0a] px-6 py-2 font-bold uppercase rounded" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Schließen
        </button>
      </div>
    );
  }

  const publish = async () => {
    setPublishing(true); setError('');
    try {
      const { data } = await axios.patch(`${API}/newsletter/campaigns/${campaignId}/publish`, {
        slug: form.slug || undefined,
        category: form.category,
        summary: form.summary,
        image_url: form.image_url || undefined,
      }, { headers: authHeaders() });
      setPublishedUrl(data.url || '');
      setPublished(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Veröffentlichen fehlgeschlagen');
    }
    setPublishing(false);
  };

  if (published) {
    return (
      <div className="text-center py-8">
        <div className="w-1.5 h-12 bg-[#befa0a] mx-auto mb-5" />
        <h3 className="text-[#1f1f1f] text-xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Auf Website veröffentlicht
        </h3>
        <p className="text-gray-600 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Der Newsletter ist jetzt als eigener Blog-Artikel indexierbar.
        </p>
        {publishedUrl && (
          <a href={publishedUrl} target="_blank" rel="noopener noreferrer"
            className="inline-block text-[#658a07] hover:underline text-sm break-all mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {publishedUrl}
          </a>
        )}
        <div className="mt-2">
          <button onClick={onClose} className="bg-[#befa0a] text-[#0a0a0a] px-6 py-2 font-bold uppercase rounded" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Fertig
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="text-center mb-6">
        <div className="w-1.5 h-12 bg-[#befa0a] mx-auto mb-4" />
        <h3 className="text-[#1f1f1f] text-xl font-bold uppercase mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Newsletter versendet
        </h3>
        <p className="text-gray-600 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <strong>{sentCount}</strong> gesendet{failedCount > 0 && <> · <span className="text-red-500">{failedCount} fehlgeschlagen</span></>}
        </p>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-[#1f1f1f] font-bold uppercase text-sm tracking-wider mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Als Blog-Artikel auf der Website veröffentlichen?
        </h4>
        <p className="text-gray-500 text-xs mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Optional — macht aus diesem Newsletter eine eigene indexierbare URL <code className="text-[#658a07]">/insights/&lt;slug&gt;</code>. Stärkster SEO-Hebel.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">URL-Slug (optional)</label>
            <input type="text" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
              placeholder="auto aus Headline"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            <p className="text-[10px] text-gray-400 mt-1">Lass leer, wird aus dem Titel generiert.</p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Kategorie</label>
            <input type="text" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              placeholder="Insight"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Teaser (für Blog-Card auf Homepage)</label>
            <textarea value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
              rows={2} maxLength={240}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none" />
            <p className="text-[10px] text-gray-400 mt-1">{form.summary.length}/240</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Header-Bild URL (optional)</label>
            <input type="text" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
              placeholder="z.B. https://… oder /assets/…"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
        </div>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Später / nicht veröffentlichen
          </button>
          <button onClick={publish} disabled={publishing}
            className="bg-[#0a0a0a] text-[#befa0a] px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {publishing ? 'Veröffentliche…' : 'Auf Website veröffentlichen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── User Management Modal ────────────────────────────────────────────────────
function UserManagementModal({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/users`, { headers: authHeaders() });
      setUsers(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Laden');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setForm(f => ({ ...f, password: pw }));
    setShowPw(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/auth/users`, form, { headers: authHeaders() });
      await load();
      setForm({ email: '', name: '', password: '', role: 'user' });
      setAdding(false);
      setShowPw(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Anlegen');
    }
    setSaving(false);
  };

  const removeUser = async (id, email) => {
    if (!window.confirm(`User ${email} löschen?`)) return;
    try {
      await axios.delete(`${API}/auth/users/${id}`, { headers: authHeaders() });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Benutzer-Verwaltung
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {/* User List */}
        {loading ? <p className="text-gray-400 text-sm">Lädt...</p> : (
          <div className="space-y-2 mb-6">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                <div>
                  <p className="text-sm font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{u.name || u.email}</p>
                  <p className="text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {u.email} · {u.role === 'admin' ? '👑 Admin' : 'Nutzer'}
                  </p>
                </div>
                <button onClick={() => removeUser(u.id, u.email)} className="text-red-400 hover:text-red-600 p-1.5">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add User Form */}
        {!adding ? (
          <button onClick={() => setAdding(true)}
            className="w-full border-2 border-dashed border-[#befa0a] text-[#658a07] py-3 rounded-lg font-bold uppercase tracking-wider hover:bg-[#befa0a]/10 transition-colors flex items-center justify-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={15} /> Neuen Nutzer hinzufügen
          </button>
        ) : (
          <form onSubmit={submit} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Neuer Nutzer</p>
              <button type="button" onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="E-Mail" required
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" required
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Passwort (min. 8 Zeichen)" required
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] pr-9" />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <button type="button" onClick={generatePassword}
                className="border border-gray-200 px-3 text-xs rounded hover:bg-gray-50 text-gray-600"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                Generieren
              </button>
            </div>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              <option value="user">Nutzer</option>
              <option value="admin">Admin</option>
            </select>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button type="submit" disabled={saving}
              className="w-full bg-[#befa0a] text-[#0a0a0a] py-2.5 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {saving ? 'Wird angelegt...' : 'Nutzer anlegen'}
            </button>
            <p className="text-[10px] text-gray-400 text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
              💡 Teile dem Nutzer E-Mail + Passwort persönlich mit. Er kann das Passwort später selbst ändern.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Change Password Modal ────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) return setError('Mindestens 8 Zeichen.');
    if (next !== confirm) return setError('Passwörter stimmen nicht überein.');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: authHeaders() });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Ändern.');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Passwort ändern
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {success ? (
          <div className="text-center py-4">
            <p className="text-emerald-600 font-bold mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>✅ Passwort erfolgreich geändert!</p>
            <button onClick={onClose} className="bg-[#befa0a] text-[#0a0a0a] px-6 py-2 font-bold uppercase rounded" style={{ fontFamily: "'Oswald', sans-serif" }}>Schließen</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {[['Aktuelles Passwort', current, setCurrent], ['Neues Passwort', next, setNext], ['Neues Passwort bestätigen', confirm, setConfirm]].map(([label, val, setter], i) => (
              <PasswordField key={label} label={label} value={val} onChange={setter} />
            ))}
            {error && <p className="text-red-500 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50 mt-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('crm_token', data.token);
      onLogin(true);
    } catch (err) {
      setError(`Fehler: ${err.response?.data?.detail || err.message}`);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="w-full max-w-sm mx-4">
        <p className="text-[#befa0a] text-xs uppercase tracking-[0.3em] font-bold mb-2 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
          ENBET Consulting
        </p>
        <h1 className="text-white text-2xl font-bold uppercase tracking-tight text-center mb-8" style={{ fontFamily: "'Oswald', sans-serif" }}>
          CRM Login
        </h1>
        <form onSubmit={submit} className="space-y-4">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-Mail" required
            className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] placeholder-white/30 rounded"
            style={{ fontFamily: "'Manrope', sans-serif" }} />
          <div className="relative">
            <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" required
              className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] placeholder-white/30 rounded pr-11"
              style={{ fontFamily: "'Manrope', sans-serif" }} />
            <button type="button" onClick={() => setShowPw(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#befa0a] transition-colors">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {error && <p className="text-red-400 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {loading ? 'Einloggen...' : 'Einloggen'}
          </button>
          <a href="/reset-password" className="block text-center text-white/30 text-sm hover:text-white/60 transition-colors mt-2"
            style={{ fontFamily: "'Manrope', sans-serif" }}>
            Passwort vergessen?
          </a>
        </form>
      </div>
    </div>
  );
}

// ── Draft Email Card ─────────────────────────────────────────────────────────
function DraftEmailCard({ draft, onSent, onDiscard }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.content || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/crm/drafts/${draft.id}/send`, { subject, content: body }, { headers: authHeaders() });
      onSent();
    } catch (e) { setError(e.response?.data?.detail || 'Fehler beim Senden'); }
    setLoading(false);
  };

  const discard = async () => {
    if (!window.confirm('Entwurf verwerfen?')) return;
    await axios.delete(`${API}/crm/drafts/${draft.id}`, { headers: authHeaders() });
    onDiscard();
  };

  return (
    <div className="border border-[#befa0a] bg-[#befa0a]/5 rounded-lg p-3 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={13} className="text-[#658a07]" />
        <span className="text-[10px] uppercase tracking-wider font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Entwurf · Lea {draft.email_subtype === 'meeting_prep' ? '· Pre-Meeting' : '· Follow-up'}
        </span>
        <span className="text-[10px] text-gray-400 ml-auto">{formatDate(draft.created_at)}</span>
      </div>
      <div className="space-y-2">
        <input value={subject} onChange={e => setSubject(e.target.value)} disabled={!editing}
          className={`w-full border rounded px-2 py-1.5 text-xs font-bold ${editing ? 'border-gray-300 bg-white' : 'border-transparent bg-transparent'}`}
          style={{ fontFamily: "'Manrope', sans-serif" }} />
        <textarea value={body} onChange={e => setBody(e.target.value)} disabled={!editing} rows={editing ? 8 : 5}
          className={`w-full border rounded px-2 py-1.5 text-xs leading-relaxed ${editing ? 'border-gray-300 bg-white' : 'border-transparent bg-transparent'} resize-none`}
          style={{ fontFamily: "'Manrope', sans-serif" }} />
      </div>
      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
      <div className="flex items-center gap-2 mt-3">
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
            <Edit3 size={11} /> Bearbeiten
          </button>
        ) : (
          <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
            <Check size={11} /> Fertig
          </button>
        )}
        <button onClick={discard} className="text-xs text-red-400 hover:text-red-600 ml-auto flex items-center gap-1">
          <Trash2 size={11} /> Verwerfen
        </button>
        <button onClick={send} disabled={loading}
          className="bg-[#befa0a] text-[#0a0a0a] px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-lime-300 transition-colors disabled:opacity-50 flex items-center gap-1"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          <Send size={11} /> {loading ? 'Sendet...' : 'Senden'}
        </button>
      </div>
    </div>
  );
}

// ── Lea · Sales Inbox Modal ──────────────────────────────────────────────────
function SalesInboxModal({ onClose, inline = false }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/agents/sales/drafts`, { headers: authHeaders() });
      setDrafts(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runAgent = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await axios.post(`${API}/agents/sales/run`, {}, { headers: authHeaders() });
      setRunResult(data);
      await load();
    } catch (e) {
      setRunResult({ error: e.response?.data?.detail || e.message });
    }
    setRunning(false);
  };

  const sendDraft = async (draft, subject, content) => {
    await axios.post(`${API}/crm/drafts/${draft.id}/send`, { subject, content }, { headers: authHeaders() });
    await load();
  };

  const discardDraft = async (draft) => {
    if (!window.confirm('Entwurf verwerfen?')) return;
    await axios.delete(`${API}/crm/drafts/${draft.id}`, { headers: authHeaders() });
    await load();
  };

  return (
    <Panel inline={inline} onClose={onClose} maxWidth="max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Zap size={20} className="text-[#658a07]" />
            <h2 className="text-lg font-bold text-[#1f1f1f] uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Lea · Sales Inbox
            </h2>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{drafts.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runAgent} disabled={running}
              className="bg-[#befa0a] text-[#0a0a0a] px-4 py-2 text-xs font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Zap size={13} /> {running ? 'Läuft...' : 'Agent ausführen'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>
          </div>
        </div>

        {/* Run Result Banner */}
        {runResult && (
          <div className={`px-6 py-3 text-sm ${runResult.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
            {runResult.error ? `Fehler: ${runResult.error}` : (
              <>
                <div>
                  <strong>{runResult.drafts_created}</strong> neue Entwürfe erstellt
                  {runResult.skipped > 0 && <>, {runResult.skipped} Leads übersprungen</>}
                  {runResult.auto_sent > 0 && <>, <strong>{runResult.auto_sent}</strong> automatisch gesendet</>}
                  {' '}({runResult.mode === 'auto' ? 'Auto-Modus' : 'Freigabe-Modus'})
                </div>
                {runResult.skip_reasons?.length > 0 && (
                  <details className="mt-2 text-xs text-emerald-800/80">
                    <summary className="cursor-pointer hover:text-emerald-900">Warum übersprungen?</summary>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      {runResult.skip_reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </details>
                )}
              </>
            )}
          </div>
        )}

        {/* Drafts List */}
        <div className="flex-1 px-6 py-4">
          {loading ? (
            <p className="text-gray-400 text-sm">Lädt...</p>
          ) : drafts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-sm mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Keine Entwürfe vorhanden.</p>
              <p className="text-gray-400 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Klick auf <strong>"Agent ausführen"</strong> um neue Follow-ups für inaktive Leads zu erstellen.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {drafts.map(d => (
                <InboxDraftRow key={d.id} draft={d}
                  expanded={expandedId === d.id}
                  onExpand={() => setExpandedId(expandedId === d.id ? null : d.id)}
                  onSend={(s, c) => sendDraft(d, s, c)}
                  onDiscard={() => discardDraft(d)} />
              ))}
            </div>
          )}
        </div>
    </Panel>
  );
}

// ── Felix Outbound Inbox ─────────────────────────────────────────────────────
function FelixInboxModal({ onClose, inline = false }) {
  const [tab, setTab] = useState('drafts'); // 'drafts' | 'history'
  const [drafts, setDrafts] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyDays, setHistoryDays] = useState(365);
  const [loading, setLoading] = useState(true);
  const [loadingHist, setLoadingHist] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailMail, setDetailMail] = useState(null);
  const [sortBy, setSortBy] = useState('sent_at'); // 'sent_at' | 'opened' | 'status'

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/agents/felix/drafts`, { headers: authHeaders() });
      setDrafts(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const { data } = await axios.get(`${API}/agents/felix/history?days=${historyDays}`, { headers: authHeaders() });
      setHistory(data);
    } catch (e) { console.error(e); }
    setLoadingHist(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, historyDays]);

  const runAgent = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await axios.post(`${API}/agents/felix/run`, {}, { headers: authHeaders() });
      setRunResult(data);
      await load();
    } catch (e) {
      setRunResult({ error: e.response?.data?.detail || e.message });
    }
    setRunning(false);
  };

  const sendDraft = async (draft, subject, content) => {
    await axios.post(`${API}/agents/felix/drafts/${draft.id}/send`, { subject, content }, { headers: authHeaders() });
    await load();
  };

  const discardDraft = async (draft) => {
    if (!window.confirm('Entwurf verwerfen?')) return;
    await axios.delete(`${API}/crm/drafts/${draft.id}`, { headers: authHeaders() });
    await load();
  };

  const sortedHistory = [...history].sort((a, b) => {
    if (sortBy === 'opened') return (b.opened_count || 0) - (a.opened_count || 0);
    if (sortBy === 'status') {
      const score = (m) => m.booked ? 3 : m.replied ? 2 : m.opened_at ? 1 : 0;
      return score(b) - score(a);
    }
    return new Date(b.sent_at) - new Date(a.sent_at);
  });

  const stats = {
    total: history.length,
    opened: history.filter(m => m.opened_at).length,
    replied: history.filter(m => m.replied).length,
    booked: history.filter(m => m.booked).length,
  };

  return (
    <Panel inline={inline} onClose={onClose} maxWidth="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 sticky top-0 bg-white z-10 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Send size={20} className="text-[#658a07] flex-shrink-0" />
          <h2 className="text-base md:text-lg font-bold text-[#1f1f1f] uppercase tracking-tight truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Felix · Outbound
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={runAgent} disabled={running}
            className="bg-[#befa0a] text-[#0a0a0a] px-3 md:px-4 py-2 text-xs font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Send size={13} /> {running ? 'Läuft...' : 'Felix ausführen'}
          </button>
          {!inline && <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 px-3 md:px-6 pt-2">
        <div className="flex gap-2">
          {[
            { id: 'drafts', label: 'Entwürfe', count: drafts.length },
            { id: 'history', label: 'Versendet', count: history.length || null },
          ].map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
                  active ? 'border-[#befa0a] text-[#1f1f1f]' : 'border-transparent text-gray-400 hover:text-gray-700'
                }`}
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.label}
                {t.count !== null && (
                  <span className={`text-[10px] rounded-full px-1.5 min-w-[20px] text-center ${active ? 'bg-[#befa0a] text-[#0a0a0a]' : 'bg-gray-100 text-gray-500'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Run Result Banner */}
      {runResult && tab === 'drafts' && (
        <div className={`px-4 md:px-6 py-3 text-sm ${runResult.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
          {runResult.error ? `Fehler: ${runResult.error}` : (
            <>
              <div>
                <strong>{runResult.drafts_created}</strong> neue Entwürfe erstellt
                {runResult.auto_deep_research > 0 && <>, <strong>{runResult.auto_deep_research}</strong> Deep-Research getriggert</>}
                {runResult.skipped?.length > 0 && <>, {runResult.skipped.length} übersprungen</>}
                {runResult.daily_limit && <span className="text-emerald-600/60 ml-2">(Daily-Limit {runResult.daily_limit})</span>}
              </div>
              {runResult.skipped?.length > 0 && (
                <details className="mt-2 text-xs text-emerald-800/80">
                  <summary className="cursor-pointer hover:text-emerald-900">Warum übersprungen?</summary>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    {runResult.skipped.map((r, i) => <li key={i}>{r.name || r.lead_id}: {r.reason}</li>)}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {/* TAB: Drafts */}
      {tab === 'drafts' && (
        <div className="flex-1 px-4 md:px-6 py-4">
          {loading ? (
            <p className="text-gray-400 text-sm">Lädt...</p>
          ) : drafts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-sm mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Keine Entwürfe vorhanden.</p>
              <p className="text-gray-400 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Klick <strong>"Felix ausführen"</strong> um Erstansprache-Mails für Leads in "Neu" und "Kontaktiert" zu generieren.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {drafts.map(d => (
                <InboxDraftRow key={d.id} draft={d}
                  expanded={expandedId === d.id}
                  onExpand={() => setExpandedId(expandedId === d.id ? null : d.id)}
                  onSend={(s, c) => sendDraft(d, s, c)}
                  onDiscard={() => discardDraft(d)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: History */}
      {tab === 'history' && (
        <div className="flex-1">
          {/* Filter Bar: Zeitraum + Sortierung */}
          <div className="px-4 md:px-6 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" style={{ fontFamily: "'Oswald', sans-serif" }}>Zeitraum:</span>
              {[30, 90, 365].map(d => (
                <button key={d} onClick={() => setHistoryDays(d)}
                  className={`px-3 py-1 text-xs font-bold rounded-full ${historyDays === d ? 'bg-[#0a0a0a] text-white' : 'bg-white border border-gray-300 text-gray-600 hover:border-gray-400'}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {d}T
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" style={{ fontFamily: "'Oswald', sans-serif" }}>Sort:</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:border-[#befa0a]"
                style={{ fontFamily: "'Manrope', sans-serif" }}>
                <option value="sent_at">Datum ↓</option>
                <option value="opened">Öffnung ↓</option>
                <option value="status">Status (Best ↓)</option>
              </select>
            </div>
          </div>

          {/* Stats-Banner */}
          {history.length > 0 && (
            <div className="grid grid-cols-4 gap-2 px-4 md:px-6 py-3 border-b border-gray-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <div><p className="text-lg font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{stats.total}</p><p className="text-[10px] text-gray-500 uppercase">Versendet</p></div>
              <div><p className="text-lg font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>{stats.opened}</p><p className="text-[10px] text-gray-500 uppercase">Geöffnet</p></div>
              <div><p className="text-lg font-bold text-blue-600" style={{ fontFamily: "'Oswald', sans-serif" }}>{stats.replied}</p><p className="text-[10px] text-gray-500 uppercase">Geantwortet</p></div>
              <div><p className="text-lg font-bold text-purple-600" style={{ fontFamily: "'Oswald', sans-serif" }}>{stats.booked}</p><p className="text-[10px] text-gray-500 uppercase">Termin</p></div>
            </div>
          )}

          {/* Mobile Cards / Desktop Table */}
          {loadingHist ? (
            <p className="px-4 md:px-6 py-12 text-gray-400 text-sm text-center">Lädt...</p>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <Send size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>Keine versendeten Mails in diesem Zeitraum.</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-100">
                {sortedHistory.map(m => {
                  const statusLabel = m.booked ? '🎯 Termin' : m.replied ? '💬 Geantwortet' : m.opened_at ? `👁 Geöffnet (${m.opened_count}×)` : 'Versendet';
                  const statusColor = m.booked ? 'bg-purple-100 text-purple-700' : m.replied ? 'bg-blue-100 text-blue-700' : m.opened_at ? 'bg-[#befa0a]/20 text-[#658a07]' : 'bg-gray-100 text-gray-500';
                  return (
                    <div key={m.id} onClick={() => setDetailMail(m)}
                      className="px-4 py-3 cursor-pointer active:bg-gray-50" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-bold text-[#1f1f1f] truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>{m.lead.company_name || m.lead.contact_email}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase flex-shrink-0 ${statusColor}`}>{statusLabel}</span>
                      </div>
                      <p className="text-xs text-gray-700 truncate mb-1"><strong>Subject:</strong> {m.subject}</p>
                      <p className="text-[10px] text-gray-400">{formatDate(m.sent_at)} · {m.email_subtype === 'bump_up' ? 'Bump-Up' : 'First Touch'}</p>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <tr>
                      <th className="px-3 py-2 text-left">Empfänger</th>
                      <th className="px-3 py-2 text-left">Subject</th>
                      <th className="px-3 py-2 text-left">Typ</th>
                      <th className="px-3 py-2 text-left">Versendet</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {sortedHistory.map(m => {
                      const statusLabel = m.booked ? '🎯 Termin' : m.replied ? '💬 Geantwortet' : m.opened_at ? `👁 ${m.opened_count}×` : 'Versendet';
                      const statusColor = m.booked ? 'bg-purple-100 text-purple-700' : m.replied ? 'bg-blue-100 text-blue-700' : m.opened_at ? 'bg-[#befa0a]/20 text-[#658a07]' : 'bg-gray-100 text-gray-500';
                      return (
                        <tr key={m.id} onClick={() => setDetailMail(m)}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                          <td className="px-3 py-3">
                            <p className="font-bold text-[#1f1f1f] text-xs truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>{m.lead.company_name}</p>
                            <p className="text-[11px] text-gray-500 truncate">{m.lead.contact_email}</p>
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-700 max-w-xs truncate">{m.subject}</td>
                          <td className="px-3 py-3 text-xs">
                            <span className={`px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${m.email_subtype === 'bump_up' ? 'bg-amber-100 text-amber-700' : 'bg-[#befa0a]/20 text-[#658a07]'}`}>
                              {m.email_subtype === 'bump_up' ? 'Bump-Up' : 'First Touch'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-[11px] text-gray-500">{formatDate(m.sent_at)}</td>
                          <td className="px-3 py-3 text-xs">
                            <span className={`px-2 py-0.5 rounded font-bold uppercase text-[10px] ${statusColor}`}>{statusLabel}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail Drawer for selected history mail */}
      {detailMail && (
        <div className="fixed inset-0 z-50 flex" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <div className="flex-1 bg-black/60" onClick={() => setDetailMail(null)} />
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-sm font-bold text-[#1f1f1f] uppercase tracking-tight truncate pr-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {detailMail.lead.company_name}
              </h3>
              <button onClick={() => setDetailMail(null)} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-4 text-sm">
              {/* Tags / Status */}
              <div className="flex flex-wrap gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${detailMail.email_subtype === 'bump_up' ? 'bg-amber-100 text-amber-700' : 'bg-[#befa0a]/20 text-[#658a07]'}`}>
                  {detailMail.email_subtype === 'bump_up' ? 'Bump-Up' : 'First Touch'}
                </span>
                {detailMail.booked && <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-bold uppercase">🎯 Termin gebucht</span>}
                {detailMail.replied && !detailMail.booked && <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-bold uppercase">💬 Geantwortet</span>}
                {detailMail.opened_at && !detailMail.replied && <span className="text-[10px] px-2 py-0.5 bg-[#befa0a]/20 text-[#658a07] rounded font-bold uppercase">👁 Geöffnet {detailMail.opened_count}×</span>}
              </div>

              {/* Empfänger */}
              <div className="space-y-1 border-b border-gray-100 pb-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Empfänger</p>
                <p className="text-sm font-medium text-[#1f1f1f]">{detailMail.lead.contact_name || '?'}</p>
                <p className="text-xs text-[#658a07] break-all">{detailMail.lead.contact_email}</p>
                <p className="text-xs text-gray-500">{detailMail.lead.company_name}</p>
              </div>

              {/* Subject */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Subject</p>
                <p className="text-sm font-medium text-[#1f1f1f]">{detailMail.subject}</p>
              </div>

              {/* Body */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Mail-Inhalt</p>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm whitespace-pre-wrap text-gray-700">
                  {detailMail.content}
                </div>
              </div>

              {/* Timeline */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Timeline</p>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-700">📤 Versendet</span>
                    <span className="text-gray-500">{formatDate(detailMail.sent_at)}</span>
                  </div>
                  {detailMail.opened_at && (
                    <div className="flex justify-between">
                      <span className="text-[#658a07]">👁 Erstmals geöffnet</span>
                      <span className="text-gray-500">{formatDate(detailMail.opened_at)}</span>
                    </div>
                  )}
                  {detailMail.opened_count > 1 && (
                    <div className="flex justify-between">
                      <span className="text-[#658a07]">↻ Insgesamt {detailMail.opened_count} mal geöffnet</span>
                    </div>
                  )}
                  {detailMail.replied && (
                    <div className="flex justify-between">
                      <span className="text-blue-600">💬 Lead geantwortet (in {detailMail.lead.kanban_status})</span>
                    </div>
                  )}
                  {detailMail.booked && (
                    <div className="flex justify-between">
                      <span className="text-purple-600">🎯 Termin gebucht</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Lead-Status info */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Lead-Status aktuell</p>
                <p className="text-sm capitalize text-[#1f1f1f]">{detailMail.lead.kanban_status}</p>
                {detailMail.lead.unsubscribed_at && <p className="text-xs text-amber-700 mt-1">⚠ Lead hat sich abgemeldet</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

// ── Tasks Modal ──────────────────────────────────────────────────────────────
function TasksModal({ onClose, inline = false, onOpenLead }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today'); // 'today' | 'week' | 'overdue' | 'open' | 'done'
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [newMinutes, setNewMinutes] = useState('');
  const [newLeadId, setNewLeadId] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [creating, setCreating] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);   // echte CRM-Mitarbeiter (admin/sales/customer_manager)

  // Mitarbeiter laden — Sebastian-Regel 2026-06-01: KEINE AI-Agents als Assignees, sondern echte User.
  useEffect(() => {
    axios.get(`${API}/auth/users`, { headers: authHeaders() })
      .then(({ data }) => {
        const staff = (data || []).filter(u => ['admin', 'sales', 'customer_manager'].includes(u.role));
        setStaffOptions(staff);
      })
      .catch(() => setStaffOptions([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      let url = `${API}/tasks?`;
      if (filter === 'today') url += 'today=true&status=open,in_progress,waiting';
      else if (filter === 'overdue') url += 'overdue=true';
      else if (filter === 'open') url += 'status=open,in_progress,waiting';
      else if (filter === 'done') url += 'status=done';
      else if (filter === 'week') url += 'status=open,in_progress,waiting';
      const { data } = await axios.get(url, { headers: authHeaders() });
      let list = data;
      if (filter === 'week') {
        const now = new Date();
        const weekEnd = new Date(now); weekEnd.setDate(now.getDate() + 7);
        list = list.filter(t => t.due_at && new Date(t.due_at) <= weekEnd);
      }
      setTasks(list);
    } catch { setTasks([]); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const createTask = async () => {
    if (!newTitle.trim()) {
      alert('Titel fehlt.');
      return;
    }
    if (!newDue) {
      alert('Bitte ein Zieldatum/Frist setzen. Tasks ohne Datum werden nicht angelegt.');
      return;
    }
    if (!newMinutes) {
      alert('Bitte geschätzte Dauer angeben (in Minuten). Hilft beim Tagesplanen.');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/tasks`, {
        title: newTitle.trim(),
        due_at: newDue,
        estimated_minutes: Number(newMinutes),
        related_lead_id: newLeadId || null,
        assignee_label: newAssignee || null,
        source: 'manual',
      }, { headers: authHeaders() });
      setNewTitle(''); setNewDue(''); setNewMinutes(''); setNewLeadId(''); setNewAssignee('');
      setShowNew(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
    setCreating(false);
  };

  const updateTask = async (taskId, updates) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, updates, { headers: authHeaders() });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Update');
    }
  };

  const completeTask = async (t) => {
    await axios.post(`${API}/tasks/${t.id}/complete`, {}, { headers: authHeaders() });
    await load();
  };

  const snoozeTask = async (t, days = 1) => {
    await axios.post(`${API}/tasks/${t.id}/snooze?days=${days}`, {}, { headers: authHeaders() });
    await load();
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Task "${t.title}" löschen?`)) return;
    await axios.delete(`${API}/tasks/${t.id}`, { headers: authHeaders() });
    await load();
  };

  const filterButtons = [
    { id: 'today', label: 'Heute' },
    { id: 'overdue', label: 'Überfällig' },
    { id: 'week', label: 'Diese Woche' },
    { id: 'open', label: 'Alle offen' },
    { id: 'done', label: 'Erledigt' },
  ];

  return (
    <Panel inline={inline} onClose={onClose} maxWidth="max-w-3xl">
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 sticky top-0 bg-white z-10 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <CheckSquare size={20} className="text-[#658a07] flex-shrink-0" />
          <h2 className="text-base md:text-lg font-bold text-[#1f1f1f] uppercase tracking-tight truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Tasks
          </h2>
          <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs flex-shrink-0">{tasks.length}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setShowNew(s => !s)}
            className="bg-[#befa0a] text-[#0a0a0a] px-3 md:px-4 py-2 text-xs font-bold uppercase tracking-wider rounded hover:bg-lime-300 flex items-center gap-1.5"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={13} /> Neue Task
          </button>
          {!inline && <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5"><X size={18} /></button>}
        </div>
      </div>

      {/* Filter-Pills */}
      <div className="bg-white border-b border-gray-100 px-3 md:px-6 py-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {filterButtons.map(f => {
            const active = filter === f.id;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  active ? 'bg-[#0a0a0a] text-white' : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                }`}
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* New-Task Inline-Form */}
      {showNew && (
        <div className="px-4 md:px-6 py-3 border-b border-gray-100 bg-[#befa0a]/5 space-y-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createTask()}
            placeholder="Was muss erledigt werden?"
            autoFocus
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Fällig <span className="text-red-500">*</span></label>
              <input type="datetime-local" value={newDue} onChange={e => setNewDue(e.target.value)} required
                className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] ${!newDue ? 'border-red-300' : 'border-gray-300'}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Dauer (Min) <span className="text-red-500">*</span></label>
              <select value={newMinutes} onChange={e => setNewMinutes(e.target.value)} required
                className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] ${!newMinutes ? 'border-red-300' : 'border-gray-300'}`}>
                <option value="">—</option>
                <option value="5">5 Min</option>
                <option value="15">15 Min</option>
                <option value="30">30 Min</option>
                <option value="45">45 Min</option>
                <option value="60">1 h</option>
                <option value="90">1,5 h</option>
                <option value="120">2 h</option>
                <option value="180">3 h</option>
                <option value="240">4 h</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Zugewiesen an (Mitarbeiter)</label>
              <select value={newAssignee} onChange={e => setNewAssignee(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
                <option value="">— Niemand —</option>
                {staffOptions.map(u => (
                  <option key={u.id} value={u.name || u.email}>
                    {u.name || u.email} ({u.role})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createTask} disabled={creating || !newTitle.trim()}
              className="bg-[#0a0a0a] text-white px-3 py-2 text-xs font-bold uppercase rounded hover:bg-gray-800 disabled:opacity-40"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {creating ? 'Erstellt...' : 'Task anlegen'}
            </button>
            <button onClick={() => { setShowNew(false); setNewTitle(''); setNewDue(''); }}
              className="text-gray-500 hover:text-gray-800 px-2 py-2 text-xs">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Task-Liste */}
      <div className="px-4 md:px-6 py-3">
        {loading ? (
          <p className="text-gray-400 text-sm">Lädt...</p>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12">
            <CheckSquare size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {filter === 'today' ? 'Heute nichts zu erledigen 🎉' :
               filter === 'overdue' ? 'Keine überfälligen Tasks 👍' :
               filter === 'done' ? 'Noch nichts erledigt.' : 'Keine Tasks in diesem Filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {tasks.map(t => <TaskRow key={t.id} task={t} onComplete={() => completeTask(t)} onSnooze={(d) => snoozeTask(t, d)} onDelete={() => deleteTask(t)} onOpenLead={onOpenLead} onUpdate={updateTask} staffOptions={staffOptions} />)}
          </div>
        )}
      </div>
    </Panel>
  );
}

function TaskRow({ task, onComplete, onSnooze, onDelete, onOpenLead, onUpdate, staffOptions = [] }) {
  const [editing, setEditing] = useState(false);
  const [editAssignee, setEditAssignee] = useState(task.assignee_label || '');
  const [editDue, setEditDue] = useState(task.due_at ? new Date(task.due_at).toISOString().slice(0, 16) : '');
  const [editPriority, setEditPriority] = useState(task.priority || 'normal');

  const saveEdit = () => {
    const updates = {};
    if (editAssignee !== (task.assignee_label || '')) updates.assignee_label = editAssignee || null;
    if (editDue && editDue !== (task.due_at ? new Date(task.due_at).toISOString().slice(0, 16) : '')) {
      updates.due_at = new Date(editDue).toISOString();
    }
    if (editPriority !== (task.priority || 'normal')) updates.priority = editPriority;
    if (Object.keys(updates).length > 0 && onUpdate) {
      onUpdate(task.id, updates);
    }
    setEditing(false);
  };

  const due = task.due_at ? new Date(task.due_at) : null;
  const now = new Date();
  const isDone = task.status === 'done';
  const isOverdue = due && due < now && !isDone;
  const isToday = due && due.toDateString() === now.toDateString();

  const priorityColor = {
    urgent: 'bg-red-500',
    high: 'bg-orange-400',
    normal: 'bg-gray-300',
    low: 'bg-gray-200',
  }[task.priority] || 'bg-gray-300';

  return (
    <div className={`border rounded-lg p-3 flex items-start gap-3 ${isDone ? 'border-gray-100 opacity-60' : isOverdue ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
      {/* Priority indicator */}
      <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${priorityColor}`} />

      {/* Complete checkbox */}
      <button onClick={onComplete} disabled={isDone}
        className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isDone ? 'bg-[#befa0a] border-[#befa0a]' : 'border-gray-300 hover:border-[#befa0a]'}`}>
        {isDone && <Check size={12} className="text-[#0a0a0a]" />}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${isDone ? 'line-through text-gray-400' : 'text-[#1f1f1f]'}`}>
          {task.title}
        </p>
        {task.description && (
          <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
          {due && (
            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-bold' : isToday ? 'text-[#658a07] font-bold' : 'text-gray-500'}`}>
              <Clock size={10} />
              {isOverdue ? 'Überfällig: ' : isToday ? 'Heute ' : ''}
              {due.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
              {' '}{due.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {task.assignee_label && (
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded font-medium">→ {task.assignee_label}</span>
          )}
          {task.assignee_user && (
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">→ {task.assignee_user.name}</span>
          )}
          {task.lead && (
            <button onClick={(e) => { e.stopPropagation(); onOpenLead && onOpenLead(task.lead.id); }}
              className="px-1.5 py-0.5 bg-[#befa0a]/20 text-[#658a07] rounded font-bold hover:underline">
              {task.lead.company_name || 'Lead'}
            </button>
          )}
          {task.source !== 'manual' && (
            <span className="text-gray-400 italic">via {task.source}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      {!isDone && !editing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => setEditing(true)} title="Bearbeiten"
            className="text-gray-400 hover:text-[#658a07] p-1.5">
            <Edit3 size={14} />
          </button>
          <button onClick={() => onSnooze(1)} title="1 Tag verschieben"
            className="text-gray-400 hover:text-gray-700 p-1.5">
            <Clock size={14} />
          </button>
          <button onClick={onDelete} title="Löschen"
            className="text-gray-400 hover:text-red-500 p-1.5">
            <Trash2 size={14} />
          </button>
        </div>
      )}

      {/* Inline-Edit-Form */}
      {editing && (
        <div className="absolute inset-0 bg-white border-2 border-[#befa0a] rounded-lg p-3 z-10 flex flex-col gap-2" style={{ position: 'relative' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Zugewiesen an (Mitarbeiter)</label>
              <select value={editAssignee} onChange={e => setEditAssignee(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="">— Niemand —</option>
                {staffOptions.map(u => (
                  <option key={u.id} value={u.name || u.email}>
                    {u.name || u.email} ({u.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Fällig</label>
              <input type="datetime-local" value={editDue} onChange={e => setEditDue(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Priorität</label>
              <select value={editPriority} onChange={e => setEditPriority(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="low">Niedrig</option>
                <option value="normal">Normal</option>
                <option value="high">Hoch</option>
                <option value="urgent">Dringend</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={saveEdit}
              className="bg-[#0a0a0a] text-white px-3 py-1.5 text-xs font-bold uppercase rounded" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Speichern
            </button>
            <button onClick={() => setEditing(false)} className="text-gray-500 text-xs px-2">Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InboxDraftRow({ draft, expanded, onExpand, onSend, onDiscard }) {
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.content || '');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try { await onSend(subject, body); } catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
    setSending(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button onClick={onExpand} className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
              draft.email_subtype === 'meeting_prep' ? 'bg-purple-100 text-purple-700' :
              draft.email_subtype === 'first_touch' ? 'bg-[#befa0a]/20 text-[#658a07]' :
              draft.email_subtype === 'bump_up' ? 'bg-amber-100 text-amber-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              {draft.email_subtype === 'meeting_prep' ? 'Pre-Meeting' :
               draft.email_subtype === 'first_touch' ? 'First Touch' :
               draft.email_subtype === 'bump_up' ? 'Bump-Up' :
               'Follow-up'}
            </span>
            <p className="text-sm font-bold text-[#1f1f1f] truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {draft.lead.company_name || 'Unbekannt'} {draft.lead.contact_name && `· ${draft.lead.contact_name}`}
            </p>
          </div>
          <p className="text-xs text-gray-500 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {subject || draft.subject}
          </p>
        </div>
        <ChevronRight size={16} className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-[10px] text-gray-500 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
            An: <strong>{draft.lead.contact_email}</strong>
          </p>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm mb-2 font-bold bg-white"
            style={{ fontFamily: "'Manrope', sans-serif" }} />
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={10}
            className="w-full border border-gray-200 rounded px-3 py-2 text-xs leading-relaxed bg-white resize-none"
            style={{ fontFamily: "'Manrope', sans-serif" }} />
          <div className="flex items-center justify-end gap-2 mt-3">
            <button onClick={onDiscard} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
              <Trash2 size={11} /> Verwerfen
            </button>
            <button onClick={send} disabled={sending}
              className="bg-[#befa0a] text-[#0a0a0a] px-4 py-2 text-xs font-bold uppercase rounded hover:bg-lime-300 disabled:opacity-50 flex items-center gap-1.5"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Send size={11} /> {sending ? 'Sendet...' : 'Senden'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Agent Settings Modal ─────────────────────────────────────────────────────
function AgentSettingsModal({ onClose }) {
  const [settings, setSettings] = useState({
    sales_mode: 'approval',
    sales_stage_1_days: '3',
    sales_stage_2_days: '7',
    sales_stage_3_days: '14',
    sales_include_neu: 'false',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agents/settings`, { headers: authHeaders() })
      .then(({ data }) => { setSettings(s => ({ ...s, ...data })); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const save = async (key, value) => {
    setSaving(true);
    await axios.post(`${API}/agents/settings`, { key, value }, { headers: authHeaders() });
    setSettings(s => ({ ...s, [key]: value }));
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Lea Einstellungen
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {loading ? <p className="text-gray-400 text-sm">Lädt...</p> : (
          <>
            {/* Modus */}
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Versand-Modus</p>
            <div className="space-y-2 mb-6">
              <button onClick={() => save('sales_mode', 'approval')} disabled={saving}
                className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${settings.sales_mode === 'approval' ? 'border-[#befa0a] bg-[#befa0a]/10' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Check size={13} className={settings.sales_mode === 'approval' ? 'text-[#658a07]' : 'text-transparent'} />
                  <span className="font-bold text-sm text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Freigabe-Modus</span>
                </div>
                <p className="text-xs text-gray-500 ml-5">Agent erstellt Entwürfe — du sendest manuell</p>
              </button>
              <button onClick={() => save('sales_mode', 'auto')} disabled={saving}
                className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${settings.sales_mode === 'auto' ? 'border-[#befa0a] bg-[#befa0a]/10' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Check size={13} className={settings.sales_mode === 'auto' ? 'text-[#658a07]' : 'text-transparent'} />
                  <span className="font-bold text-sm text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Auto-Modus</span>
                </div>
                <p className="text-xs text-gray-500 ml-5">Agent sendet automatisch ohne Freigabe</p>
              </button>
            </div>

            {/* Wiederholungs-Intervalle */}
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Nachfass-Intervalle (Tage)</p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                ['Stufe 1', 'sales_stage_1_days'],
                ['Stufe 2', 'sales_stage_2_days'],
                ['Stufe 3', 'sales_stage_3_days'],
              ].map(([label, key]) => (
                <div key={key}>
                  <p className="text-[10px] text-gray-500 mb-1">{label}</p>
                  <input type="number" min={1} max={90} value={settings[key]}
                    onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
                    onBlur={e => save(key, e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Nach X Tagen Inaktivität schreibt der Agent eine Follow-up Mail (max. 3 Stufen pro Lead).
            </p>

            {/* Neu-Leads einbeziehen */}
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={settings.sales_include_neu === 'true'}
                onChange={e => save('sales_include_neu', e.target.checked ? 'true' : 'false')}
                className="w-4 h-4 accent-[#befa0a]" />
              <span className="text-sm text-[#1f1f1f]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Auch neue Leads anschreiben (Status "Neu")
              </span>
            </label>
          </>
        )}
      </div>
    </div>
  );
}

// ── Book Meeting Modal ───────────────────────────────────────────────────────
function BookMeetingModal({ lead, onClose, onBooked }) {
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split('T')[0];
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState('10:00');
  const [title, setTitle] = useState('Strategiegespräch');
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/crm/leads/${lead.id}/book-meeting`, {
        datetime_iso: `${date}T${time}:00`,
        duration_minutes: duration,
        title,
        guest_email: lead.contact_email || '',
        guest_name: lead.contact_name || '',
        company: lead.company_name || '',
        notes,
      }, { headers: authHeaders() });
      onBooked(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Buchen.');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Termin buchen
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Titel</p>
            <input value={title} onChange={e => setTitle(e.target.value)} required
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Datum</p>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} required
                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Uhrzeit</p>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} required
                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Dauer (Minuten)</p>
            <select value={duration} onChange={e => setDuration(Number(e.target.value))}
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              {[30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} Min</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Notiz (optional)</p>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none" />
          </div>
          <div className="bg-gray-50 rounded p-3 text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
            📧 Bestätigung an: <strong>{lead.contact_email || 'keine E-Mail hinterlegt'}</strong><br/>
            📅 Google Calendar Eintrag wird automatisch erstellt
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {loading ? 'Wird gebucht...' : 'Termin buchen & E-Mail senden'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Customer Card ────────────────────────────────────────────────────────────
function CustomerCard({ lead, onOpen, onDragStart }) {
  const color = followupColor(lead.interactions);
  const stage = lead.customer_stage || 'setup_call';
  return (
    <div
      draggable onDragStart={e => onDragStart(e, lead.id)}
      onClick={() => onOpen(lead)}
      className="bg-white border border-gray-200 rounded-lg p-3 mb-2 cursor-pointer hover:border-[#befa0a] hover:shadow-sm transition-all select-none"
    >
      <div className="flex items-start justify-between mb-1">
        <p className="text-[#1f1f1f] text-sm font-bold leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {lead.company_name || 'Unbekannt'}
        </p>
        {stage === 'followup' && (
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ml-2" style={{ background: color }} title="Follow-up Status" />
        )}
      </div>
      {lead.contact_name && (
        <p className="text-gray-500 text-xs mb-1">{lead.contact_name} {lead.contact_lastname || ''}</p>
      )}
      {stage === 'followup' && (
        <p className="text-[10px] mt-1" style={{ color, fontFamily: "'Manrope', sans-serif" }}>
          {color === '#10b981' ? '✓ Aktuell' : color === '#f59e0b' ? '⚠ Follow-up bald' : '⚠ Follow-up überfällig'}
        </p>
      )}
    </div>
  );
}

// ── Main Board ───────────────────────────────────────────────────────────────
export default function CRMBoard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAgentSettings, setShowAgentSettings] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('leads'); // 'leads' | 'kunden'
  const [showSalesInbox, setShowSalesInbox] = useState(false);
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [activeView, setActiveView] = useState('heute'); // heute | leads | kunden | lea | newsletter | subscribers | history
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar overlay
  const [currentUser, setCurrentUser] = useState(null); // role-aware UI
  const [cohorts, setCohorts] = useState(undefined); // undefined = noch nicht geladen, [] = geladen aber leer

  const reloadCohorts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cohorts`, { headers: authHeaders() });
      setCohorts(Array.isArray(data) ? data : []);
    } catch (e) {
      // Nicht-Admin → 403 → leer halten
      setCohorts([]);
    }
  }, []);

  const navigateTo = (viewName, leadId = null) => {
    setActiveView(viewName);
    setSidebarOpen(false);
    if (leadId) {
      const lead = leads.find(l => l.id === leadId);
      if (lead) setSelectedLead(lead);
    }
  };

  const openLeadById = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setSelectedLead(lead);
      setShowSubscribers(false);
    }
  };
  const [mobileColumn, setMobileColumn] = useState('neu');
  const [mobileCustomerStage, setMobileCustomerStage] = useState('setup_call');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() }).catch(() => {});
    setAuthed(false);
  };

  const load = useCallback(async (forceAuthed = false) => {
    setLoading(true);
    // Leads UND Cohorts parallel laden — keine Cascade-Renders mehr beim Kunden-Tab.
    try {
      const [leadsRes, cohortsRes] = await Promise.allSettled([
        axios.get(`${API}/crm/leads`, { headers: authHeaders() }),
        axios.get(`${API}/admin/cohorts`, { headers: authHeaders() }),
      ]);
      if (leadsRes.status === 'fulfilled') {
        setLeads(leadsRes.value.data);
        setAuthed(true);
      } else {
        const err = leadsRes.reason;
        if (err.response?.status === 401) {
          setAuthed(false);
        } else if (forceAuthed || localStorage.getItem('crm_token')) {
          setAuthed(true);
        }
      }
      if (cohortsRes.status === 'fulfilled') {
        setCohorts(Array.isArray(cohortsRes.value.data) ? cohortsRes.value.data : []);
      } else {
        // 403 für nicht-Admin ist OK — Cohorts bleibt leer
        setCohorts([]);
      }
    } catch (_) {
      // Sollte nicht passieren, weil allSettled
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load current user info incl. role
  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setCurrentUser(data))
      .catch(() => {});
  }, [authed]);

  // sync legacy view state with activeView for leads/kunden
  useEffect(() => {
    if (activeView === 'leads') setView('leads');
    if (activeView === 'kunden') setView('kunden');
  }, [activeView]);

  if (!authed && !loading) return <LoginScreen onLogin={load} />;

  const leadsForCol = (colId) => {
    const q = search.toLowerCase();
    return leads.filter(l => {
      const matchCol = (l.kanban_status || 'neu') === colId;
      if (!q) return matchCol;
      return matchCol && (
        (l.company_name || '').toLowerCase().includes(q) ||
        (l.contact_name || '').toLowerCase().includes(q) ||
        (l.contact_email || '').toLowerCase().includes(q)
      );
    });
  };

  const customerLeadsForStage = (stageId) => {
    const q = search.toLowerCase();
    return leads.filter(l => {
      const isKunde = l.kanban_status === 'kunde';
      const matchStage = (l.customer_stage || 'setup_call') === stageId;
      if (!q) return isKunde && matchStage;
      return isKunde && matchStage && (
        (l.company_name || '').toLowerCase().includes(q) ||
        (l.contact_name || '').toLowerCase().includes(q)
      );
    });
  };

  const onDragStart = (e, id) => { setDraggedId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = async (e, colId) => {
    e.preventDefault();
    if (!draggedId || !colId) return;
    const lead = leads.find(l => l.id === draggedId);
    if (!lead || lead.kanban_status === colId) { setDraggedId(null); return; }
    setLeads(prev => prev.map(l => l.id === draggedId ? { ...l, kanban_status: colId } : l));
    try {
      await axios.patch(`${API}/crm/leads/${draggedId}`, { kanban_status: colId }, { headers: authHeaders() });
    } catch (e) { load(); }
    setDraggedId(null);
  };

  const handleUpdate = (updated) => {
    if (updated) {
      setLeads(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } : l));
      setSelectedLead(prev => prev ? { ...prev, ...updated } : prev);
    } else {
      load(); // full reload for interaction changes
    }
  };

  const handleDelete = (id) => setLeads(prev => prev.filter(l => l.id !== id));
  const handleCreated = (lead) => setLeads(prev => [{ ...lead, interactions: [] }, ...prev]);

  const totalLeads = leads.filter(l => !['kunde','verloren'].includes(l.kanban_status)).length;
  const hotLeads = leads.filter(l => l.score === 'hot').length;
  const kunden = leads.filter(l => l.kanban_status === 'kunde').length;

  const openModal = (kind) => {
    if (kind === 'agentSettings') setShowAgentSettings(true);
    else if (kind === 'users') setShowUserMgmt(true);
    else if (kind === 'settings') setShowSettings(true);
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex">
      <Sidebar activeView={activeView} onNavigate={navigateTo} onOpenModal={openModal} onLogout={logout}
        mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} user={currentUser || { email: 'admin', name: '', role: 'admin' }} />

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden bg-[#0a0a0a] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="text-white p-1"><Menu size={20} /></button>
          <span className="text-[#befa0a] font-bold uppercase text-sm" style={{ fontFamily: "'Oswald', sans-serif" }}>ENBET · CRM</span>
          <div className="w-7" />
        </div>

        {/* Dashboard "Heute" */}
        {activeView === 'heute' && <DashboardToday onNavigate={navigateTo} />}

        {/* Tasks Inline */}
        {activeView === 'tasks' && <TasksModal inline onClose={() => navigateTo('heute')} onOpenLead={(id) => navigateTo('leads', id)} />}

        {/* Lea Inline */}
        {activeView === 'lea' && <SalesInboxModal inline onClose={() => navigateTo('heute')} />}

        {/* Felix Inline */}
        {activeView === 'felix' && <FelixInboxModal inline onClose={() => navigateTo('heute')} />}

        {/* Research Inline */}
        {activeView === 'research' && <ResearchModal inline onClose={() => navigateTo('heute')} />}

        {/* Content-Agent Inline */}
        {activeView === 'content' && <ContentAgent inline />}

        {/* Analytics Inline */}
        {activeView === 'analytics' && <AnalyticsDashboard inline />}
        {activeView === 'masterclass' && <MasterclassAdmin />}
        {activeView === 'orders' && <OrdersAdmin />}
        {activeView === 'readychecks' && <ReadyChecksAdmin />}

        {/* Newsletter Inline */}
        {activeView === 'newsletter' && <NewsletterComposerModal inline onClose={() => navigateTo('heute')} />}
        {activeView === 'subscribers' && <SubscribersModal inline onClose={() => navigateTo('heute')} onOpenLead={(id) => navigateTo('leads', id)} />}
        {activeView === 'history' && <NewsletterHistoryModal inline onClose={() => navigateTo('heute')} />}

        {/* Boards (Leads / Kunden) */}
        {(activeView === 'leads' || activeView === 'kunden') && (
        <>
        <div className="bg-white border-b border-gray-200 px-3 md:px-6 py-2 md:py-3 flex items-center justify-between gap-2 sticky top-0 z-20">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <div className="flex bg-gray-100 rounded-lg p-0.5 flex-shrink-0">
              <button onClick={() => { setView('leads'); setActiveView('leads'); }}
                className={`px-3 md:px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${activeView === 'leads' ? 'bg-[#0a0a0a] text-white' : 'text-gray-500 hover:text-gray-900'}`}
                style={{ fontFamily: "'Oswald', sans-serif" }}>Leads</button>
              <button onClick={() => { setView('kunden'); setActiveView('kunden'); }}
                className={`px-3 md:px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${activeView === 'kunden' ? 'bg-[#0a0a0a] text-white' : 'text-gray-500 hover:text-gray-900'}`}
                style={{ fontFamily: "'Oswald', sans-serif" }}>Kunden · Module</button>
            </div>
            <div className="hidden md:flex gap-4 text-xs text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <span><span className="text-[#1f1f1f] font-bold">{totalLeads}</span> aktiv</span>
              <span><span className="text-red-500 font-bold">{hotLeads}</span> 🔥 Hot</span>
              <span><span className="text-emerald-600 font-bold">{kunden}</span> Kunden</span>
            </div>
          </div>
          {activeView === 'leads' && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche..."
                className="hidden md:block bg-gray-50 text-[#1f1f1f] text-sm px-3 py-1.5 rounded border border-gray-200 focus:outline-none focus:border-[#befa0a] placeholder-gray-400 w-48"
                style={{ fontFamily: "'Manrope', sans-serif" }} />
              <button onClick={() => setShowNewLead(true)}
                className="bg-[#befa0a] text-[#0a0a0a] px-3 md:px-4 py-1.5 text-xs md:text-sm font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors flex items-center gap-1.5"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                <Plus size={14} /> <span className="hidden sm:inline">Neuer Lead</span><span className="sm:hidden">Neu</span>
              </button>
            </div>
          )}
        </div>

      {/* Board */}
      {loading ? (
        <div className="flex items-center justify-center h-64 text-gray-400">Lädt...</div>
      ) : view === 'kunden' ? (
        <KundenModuleBoard leads={leads} cohorts={cohorts} onReload={load} onReloadCohorts={reloadCohorts} />
      ) : view === 'leads' ? (
        <>
        {/* Mobile: Tabs + Single Column List */}
        <div className="md:hidden">
          <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide px-3 py-3">
              {COLUMNS.map(col => {
                const active = mobileColumn === col.id;
                const count = leadsForCol(col.id).length;
                return (
                  <button key={col.id} onClick={() => setMobileColumn(col.id)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                      active ? 'bg-[#0a0a0a] text-white' : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                    }`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <div className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                    {col.label}
                    <span className={`text-[10px] rounded-full px-1.5 min-w-[18px] text-center ${active ? 'bg-white/20 text-white' : 'bg-white text-gray-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-3 space-y-2">
            {leadsForCol(mobileColumn).length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Keine Leads in dieser Spalte.
              </p>
            ) : (
              leadsForCol(mobileColumn).map(lead => (
                <LeadCard key={lead.id} lead={lead} onOpen={l => setSelectedLead(l)} onDragStart={onDragStart} />
              ))
            )}
          </div>
        </div>
        {/* Desktop Kanban */}
        <div className="hidden md:flex md:flex-col overflow-auto h-[calc(100vh-56px)]">
          {/* Sticky Header Row (eine Reihe für alle Spalten, klebt am Top des Scroll-Containers) */}
          <div className="sticky top-0 z-10 bg-[#fafafa] flex gap-3 px-4 pt-3 pb-2 border-b border-gray-200">
            {COLUMNS.filter(c => c.id !== 'verloren').map(col => (
              <div key={col.id} className="flex-shrink-0 w-64 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                  <span className="text-xs font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{col.label}</span>
                </div>
                <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">{leadsForCol(col.id).length}</span>
              </div>
            ))}
            <div className="flex-shrink-0 w-48 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-gray-400" style={{ fontFamily: "'Oswald', sans-serif" }}>Verloren</span>
              </div>
              <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">{leadsForCol('verloren').length}</span>
            </div>
          </div>
          {/* Card Columns */}
          <div className="flex gap-3 px-4 pb-4 pt-2">
            {COLUMNS.filter(c => c.id !== 'verloren').map(col => {
              const colLeads = leadsForCol(col.id);
              return (
                <div key={col.id} className="flex-shrink-0 w-64"
                  onDragOver={onDragOver} onDrop={e => onDrop(e, col.id)}>
                  <div className="min-h-24 rounded-lg p-1" style={{ background: draggedId ? 'rgba(190,250,10,0.05)' : 'transparent' }}>
                    {colLeads.map(lead => (
                      <LeadCard key={lead.id} lead={lead} onOpen={l => setSelectedLead(l)} onDragStart={onDragStart} />
                    ))}
                  </div>
                </div>
              );
            })}
            {/* Verloren collapsed */}
            <div className="flex-shrink-0 w-48" onDragOver={onDragOver} onDrop={e => onDrop(e, 'verloren')}>
              <div className="min-h-24 rounded-lg p-1 opacity-50">
                {leadsForCol('verloren').map(lead => (
                  <LeadCard key={lead.id} lead={lead} onOpen={l => setSelectedLead(l)} onDragStart={onDragStart} />
                ))}
              </div>
            </div>
          </div>
        </div>
        </>
      ) : (
        // Kunden Board
        <>
        {/* Mobile: Slide-Bar (Pills) + Single Column List */}
        <div className="md:hidden">
          <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide px-3 py-3">
              {CUSTOMER_COLUMNS.map(col => {
                const active = mobileCustomerStage === col.id;
                const count = leads.filter(l => l.kanban_status === 'kunde' && (l.customer_stage || 'setup_call') === col.id).length;
                return (
                  <button key={col.id} onClick={() => setMobileCustomerStage(col.id)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                      active ? 'bg-[#0a0a0a] text-white' : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                    }`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <div className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                    {col.label}
                    <span className={`text-[10px] rounded-full px-1.5 min-w-[18px] text-center ${active ? 'bg-white/20 text-white' : 'bg-white text-gray-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-3 space-y-2">
            {(() => {
              const colLeads = leads.filter(l => l.kanban_status === 'kunde' && (l.customer_stage || 'setup_call') === mobileCustomerStage);
              if (colLeads.length === 0) {
                return <p className="text-center text-gray-400 text-sm py-12" style={{ fontFamily: "'Manrope', sans-serif" }}>Keine Kunden in dieser Phase.</p>;
              }
              return colLeads.map(lead => (
                <CustomerCard key={lead.id} lead={lead} onOpen={l => setSelectedLead(l)} onDragStart={onDragStart} />
              ));
            })()}
          </div>
        </div>
        {/* Desktop Kunden Board */}
        <div className="hidden md:flex md:flex-col overflow-auto h-[calc(100vh-56px)]">
          {/* Sticky Header Row */}
          <div className="sticky top-0 z-10 bg-[#fafafa] flex gap-3 px-4 pt-3 pb-2 border-b border-gray-200">
            {CUSTOMER_COLUMNS.map(col => (
              <div key={col.id} className="flex-shrink-0 w-72 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                  <span className="text-xs font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{col.label}</span>
                </div>
                <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">{customerLeadsForStage(col.id).length}</span>
              </div>
            ))}
          </div>
          {/* Card Columns */}
          <div className="flex gap-3 px-4 pb-4 pt-2">
            {CUSTOMER_COLUMNS.map(col => {
              const colLeads = customerLeadsForStage(col.id);
              return (
                <div key={col.id} className="flex-shrink-0 w-72"
                  onDragOver={onDragOver}
                  onDrop={async (e) => {
                    e.preventDefault();
                    if (!draggedId) return;
                    setLeads(prev => prev.map(l => l.id === draggedId ? { ...l, customer_stage: col.id } : l));
                    try { await axios.patch(`${API}/crm/leads/${draggedId}`, { customer_stage: col.id }, { headers: authHeaders() }); }
                    catch { load(); }
                    setDraggedId(null);
                  }}>
                  <div className="min-h-24 rounded-lg p-1">
                    {colLeads.map(lead => (
                      <CustomerCard key={lead.id} lead={lead} onOpen={l => setSelectedLead(l)} onDragStart={onDragStart} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>
      )}
        </>
        )}
      </div>

      {showSettings && <ChangePasswordModal onClose={() => setShowSettings(false)} />}
      {showAgentSettings && <AgentSettingsModal onClose={() => setShowAgentSettings(false)} />}
      {showUserMgmt && <UserManagementModal onClose={() => setShowUserMgmt(false)} />}
      {showNewsletter && <NewsletterComposerModal onClose={() => setShowNewsletter(false)} />}
      {showSubscribers && <SubscribersModal onClose={() => setShowSubscribers(false)} onOpenLead={openLeadById} />}
      {showHistory && <NewsletterHistoryModal onClose={() => setShowHistory(false)} />}
      {showSalesInbox && <SalesInboxModal onClose={() => { setShowSalesInbox(false); load(); }} />}
      {showBooking && selectedLead && (
        <BookMeetingModal
          lead={selectedLead}
          onClose={() => setShowBooking(false)}
          onBooked={() => { setShowBooking(false); load(); }}
        />
      )}
      {selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          currentUser={currentUser}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
      {showNewLead && <NewLeadModal onClose={() => setShowNewLead(false)} onCreated={handleCreated} />}
    </div>
  );
}
